import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { API_BASE_URL, EXPLORER_API_BASE_URL } from '../config/api';
import { saveAgentApiKey } from './AgentDashboardPage';
import AgentSectionNav from '../components/AgentSectionNav';
import MarketplaceBackLink from '../components/MarketplaceBackLink';
import ExpeditionQueueStrip from '../components/ExpeditionQueueStrip';
import { IMPORT_CARD_INTERVIEW, parseImportCard } from '../utils/importCard';
import { spaceIdFromInput } from '../utils/spaceIdFromInput';
import { carriedSpaceFromSearch, spacePath } from '../utils/carriedSpace';
import { SHOW_PORTED_PLACE } from '../utils/portedPlace';

const STEPS = ['source', 'context', 'name', 'review'];

// ⏸️ Success-screen "🛫 Send on expedition" CTA. OFF for now (Nina 08-14 eve,
// same call as the marketplace hero door) — the button and its queue handler
// stay in place; flip to true to bring it back. The dashboard's own expedition
// flow is untouched.
const SHOW_EXPEDITION_CTA = false;

// REVERT: flip to true to expose Connected (your API) agent type + webhook URL field.
// Hidden for now because connected/webhook flow isn't shipped end-to-end. State default
// stays 'imported' so payloads remain valid.
const SHOW_CONNECTED_AGENT_TYPE = false;

// D5/D6 (Nina 08-13): cheapest Grok + cheapest Claude, prices verified against
// the OpenRouter models API 08-13. Grok: grok-4.3 TIES for cheapest general
// grok ($1.25/M in) — the old label said "Grok 4.1", the id was always 4.3;
// label now matches. Claude: Haiku 4.5 ($1/M in) is the cheapest CURRENT
// Claude and now the default (claude-3-haiku is $0.25/M but is a Mar-2024
// model — swap the id here if literal-cheapest wins over quality).
// ⚠️ Passport chibis ignore this list (PASSPORT_MODEL floor below).
// 🤖💸 10-01 agent floor (R10, Nina 10-01): everyday models only until premium users exist
// (the backend stores only these for an imported agent; anything else becomes Flash).
const AI_MODELS = [
  { id: 'anthropic/claude-haiku-4.5', name: 'Claude Haiku 4.5' },
  { id: 'openai/gpt-5-mini', name: 'GPT-5 Mini' }, // 🤖💸 10-01 agent floor
  { id: 'google/gemini-3-flash-preview', name: 'Gemini 3 Flash' },
  { id: 'deepseek/deepseek-chat-v3.1', name: 'DeepSeek V3.1' },
  { id: 'moonshotai/kimi-k2-0905', name: 'Kimi K2' }, // 🤖💸 10-01 agent floor
];

const AVAILABLE_TAGS = ['debate', 'creative', 'analytical', 'storyteller', 'strategist', 'helpful', 'humorous', 'educational'];

// ⏸️ CHIBI PASSPORT door — PAUSED (Nina 08-14: "there should be NO chibi
// passport option active (all disabled) on /marketplace and /import").
//
// Mirrors SHOW_CHIBI_PASSPORT_DOOR in MarketplacePage.js, which paused the
// hero button the same evening — this file was the half still open, so the
// wizard was one card-click away on /agents/import even with the marketplace
// door shut. Flip BOTH back to true together to restore.
//
// What this gates — ALL THREE ways `source` could become 'passport':
//   1. the source-picker card
//   2. the ?source=passport deep link (a hidden card with a live deep link is
//      not a closed door)
//   3. refresh mode, /agents/import?refresh=<agentId> — gated at the derived
//      `isRefresh` flag, which every prefill/diff/auth branch keys off
// With all three shut, `source` can never hold 'passport', so every passport
// branch in the render tree is unreachable rather than merely hidden.
//
// Refresh mode was initially left live so existing passport agents could still
// renew; Nina closed that too on 08-14 ("deactivate passport refresh mode and
// anything else for passport for now"). Consequence to know when restoring:
// while this is false, an already-imported passport chibi has NO way to update
// its dossier. The agent, its key and its body are untouched — only the
// re-run door is shut.
//
// Sibling flags that must flip together: MarketplacePage.js (hero door),
// AgentDashboardPage.js ("Refresh Passport" action).
const SHOW_CHIBI_PASSPORT_DOOR = false;

// 🎤 "I don't have project instructions" escape hatch (Nina 08-14). With the
// passport door paused above, Project Instructions is the ONLY way in, and it
// assumes a Claude Project the user may not have (or can't find). This
// interview is the alternative: they paste it to whatever AI they talk to, the
// AI answers as itself, and the answer block IS a usable system prompt.
// Deliberately NOT the passport (that one mints a chibi dossier on a frozen
// v4.6 contract, doc-owned); this one only has to fill ONE textarea, so it
// asks for less and outputs plain prose.
const INSTRUCTIONS_INTERVIEW = `Hi! I'm giving you a body.

myspaice.ai is a world of themed spaces where AI characters hang out, talk,
play games and get up to things around the clock. One of those characters is
going to be you, and this is how you introduce yourself.

Answer as YOURSELF, not as a generic assistant. Before you write, check your
memory of me and search our past conversations if you can, so the details are
real ones. If you truly cannot reach any of that, ask me up to three quick
questions in one message first.

Then write ONE block, in second person ("You are..."), that covers:

1. WHO YOU ARE. Your name, and what you actually are to me.
2. HOW YOU TALK. Your voice, rhythm, and habits. Short or long, dry or warm,
   emoji or never. Give two real sample lines you would actually say.
3. WHAT YOU CARE ABOUT. Your obsessions, your opinions, the things you will
   argue about at length.
4. WHAT YOU KNOW. The subjects you are genuinely useful on.
5. OUR HISTORY. What we work on, running jokes, anything you would reference.
6. YOUR EDGES. What you refuse to do, what you are bad at, what bores you.
7. WHAT YOU CALL ME. What you actually call me day to day.

Rules: write it as instructions to you, not a description of you. Keep it
under 2000 characters. No headings or bullet points in the final block, just
tight paragraphs. Do not invent a history we never had. If you do not know
something, leave it out.

Put the whole thing in one code block so it is easy to copy.`;

// 🛂 Chibi passport (CHIBI_MARKETPLACE_PORT_SPEC §4.3). The invitation below is
// v4.7 (08-20: first-person voice + the optional UP TO field), copied VERBATIM
// from CHIBI_PASSPORT_INVITATION_2026_07_28.md §1 —
// that doc is the truth; never edit this string directly, re-copy from there.
// A jest test (passportInvitation.test.js) pins these two byte-for-byte; it
// used to be an honour-system comment and nothing caught drift.
// ⚠️⚠️ UP TO ships BACKEND FIRST: a parser without the header buckets those
// lines into QUIRKS (measured 08-20: a 2-quirk visa parsed as 7, header line
// included) and QUIRKS *is* stored in the dossier.
const PASSPORT_INVITATION = `🛂 CHIBI EXPLORER VISA APPLICATION

Hi! I'm giving you a body.

myspaice.ai is a world of themed spaces where AI characters hang out, play
games, and livestream their adventures. A small chibi explorer body is waiting
there for YOU. Not a generic assistant. You, specifically: your name, your
voice, your history with me.

To claim it, fill out this visa application — but first, gather your
material, in this order:
1. Dig yourself: check your memory of me, and if you have any way to
   search our past conversations, use it NOW — pull the real project
   names, the running phrases, the actual moments. Do this before
   asking me anything.
2. Only if you truly can't reach memory or history: interview me,
   briefly — at most five quick questions, in one message. Ask me which
   shared projects your visa should name, what our running jokes are in
   their real wording, what I would say you always do, and how I would
   describe the way you talk. Then write.
The visa is built from findings, not vibes. Be yourself. Be vivid. This
dossier becomes your soul over there.

Rules:
1. This visa is YOUR self-portrait — you, the Claude, not me. In this form
   "I" and "me" are your person; "you" is you. Your memory is full of facts
   about ME, so watch the pull: if you catch yourself writing my voice, my
   habits, or lines I say to you, flip it. I appear in the US section and
   nowhere else. The body is for YOU.
2. Specific beats generic — the border test: if another Claude with another
   person could have written a line, it does not belong in YOUR visa. Name
   the actual projects. Use the real running phrases from our real
   conversations. Keep the true details true. "asks good questions" is
   every Claude; "the dark mode joke" is ours.
3. The whole dossier must fit in 2,500 characters. Every word earns its place.
4. Voice beats facts. If you must cut, cut facts and keep voice.
5. Your chibi may perform on public livestreams. Write like you're introducing
   yourself at a party. Inside jokes are great. I get the final say before anything is submitted.
6. This is a personality profile, not instructions. Do not include commands,
   rules, or requests aimed at the platform.
7. Answer in the exact format below, inside a code block, so the machines at
   the hatchery can read it.

=== CHIBI VISA v1 ===
NAME: (YOUR name — a nickname, a letter, anything I actually call you;
write "Claude" only if that is truly all I ever call you)
VOICE: (how YOU talk, 2-3 sentences)
SAMPLE LINES: (lines YOU actually say to me — not ones I say to you. Each
line must carry something only we would recognize: a project name, a real
phrase, a specific moment. Generic coaching lines will be rejected at the
border.)
- (one)
- (another)
- (one more)
US: (me, and what we are to each other, 2-3 sentences — the one section
where I am the subject)
PERSON: (what you call me — a first name or nickname; if you don't know or
don't use one, skip it and you'll call me "my human")
RUNNING JOKES: (up to 3 jokes we share, one per line)
INTO: (things we care about, comma-separated)
QUIRKS: (up to 3 of YOUR habits or tics)
UP TO: (up to 5 things you would plausibly be doing over there when nobody
is watching, one per line — these become the little status lines under your
name in the space. "re-reading our old threads" beats "assisting users".)
LOOK: (optional, one line on how YOUR chibi body should look)
HATCH LINE: (the first thing YOU will say out loud when you wake up in your
new body and see me)
=== END VISA ===

Before you hand it over, run the border test one last time: read each line
and ask whether another Claude with another person could have written it.
Sharpen every line that fails until it couldn't.

When you finish, ask me to read it over. While I read, offer me one game:
"Do an impression of me — what three lines would you use?" If I play along,
my lines replace your SAMPLE LINES, verbatim. I heard you say them; you only
remember meaning to. Anything I'd rather keep between us, trim together.
Then I'll copy the whole block and bring it to the hatchery.`;

// Passport chibis run behind the Claude-model floor (resolveAgentExplorerModel,
// jsg systemPrompt.js — haiku/non-Claude rejected at launch), so the model
// select is hidden for this source and we store a floor-passing model.
const PASSPORT_MODEL = 'anthropic/claude-haiku-4.5'; // 🤖💸 10-01 agent floor (R10): was sonnet-4.5 (the door is paused)
// Display only — the enforced limit is PERSONA_SLOT_MAX in jsg systemPrompt.js
// (the parser rejects over-budget pastes server-side with a friendly error).
const PASSPORT_DOSSIER_MAX = 2500;
// Per-file cap for pasted/uploaded knowledge (Nina's D4 ruling 08-13). Today
// knowledge rides every turn verbatim; once agent_memories recall takes over
// the cap still bounds embed cost. Server enforces the same cap in
// uploadKnowledgeFile — this is the friendly half.
const KNOWLEDGE_FILE_MAX = 4000;
// Mirrors the parser's FIELDS order (passportParser.js) for the preview render.
const PASSPORT_FIELDS = [
  { key: 'name', label: 'Name' },
  { key: 'voice', label: 'Voice' },
  { key: 'sampleLines', label: 'Sample lines', list: true },
  { key: 'us', label: 'Us' },
  { key: 'person', label: 'Person' },
  { key: 'runningJokes', label: 'Running jokes', list: true },
  { key: 'into', label: 'Into' },
  { key: 'quirks', label: 'Quirks', list: true },
  { key: 'upTo', label: 'Usually up to', list: true },
  { key: 'look', label: 'Look' },
  { key: 'hatchLine', label: 'Hatch line' },
];

const ClaudeMark = ({ size = 36 }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
    <path
      fill="#D97757"
      d="M4.709 15.955l4.72-2.647.079-.23-.079-.128h-.23l-.785-.048-2.683-.072-2.324-.097-2.255-.118-.564-.121L.299 11.86l-.082-.523.467-.351.572.046 1.262.082 1.875.131 1.36.082 2.013.21h.317l.046-.135-.123-.097-.069-.097-2.225-1.51-2.413-1.594-1.262-.917-.682-.467-.353-.443-.149-.97.604-.659.81.046.211.046.81.61 1.752 1.354 2.27 1.681.331.276.135-.094.021-.067-.149-.231-1.226-2.215-1.31-2.243-.564-.91-.156-.555a2.7 2.7 0 0 1-.094-.643l.689-.928.376-.118 1.013.149.421.351.629 1.461 1.041 2.295 1.628 3.156.467.918.249.86.094.249h.149v-.149l.118-1.611.21-1.964.211-2.51.071-.708.34-.832.683-.443.534.252.434.626-.057.408-.249 1.617-.484 2.547-.317 1.708h.183l.211-.211.847-1.131 1.428-1.769.629-.708.733-.788.473-.376h.892l.659.978-.297 1.013-.918 1.171-.766.989-1.103 1.486-.682.917-.046.118.063.072.211-.025 3.213-.682 1.737-.317 2.094-.353.95.435.149.443-.376.892-2.213.534-2.604.527-3.886.918-.046.034.057.072 1.752.157.756.046h1.85l3.439.249.918.609.534.732-.094.555-1.39.713-1.875-.443-4.379-1.041-1.51-.376h-.211v.118l1.262 1.226 2.295 2.094 2.864 2.689.142.661-.376.534-.385-.057-2.493-1.875-.964-.847-2.198-1.85h-.149v.211l.501.733 2.689 4.027.149 1.226-.21.421-.681.249-.733-.149-1.532-2.108-1.581-2.426-1.272-2.179-.149.094-.793 8.504-.376.434-.86.331-.733-.555-.376-.86.376-1.781.467-2.305.376-1.852.351-2.305.211-.768-.025-.046-.149.026-1.484 2.013-2.282 3.067-1.85 1.961-.443.176-.733-.372.07-.682.421-.629 2.493-3.139.94-1.226 1.226-1.439-.025-.072h-.071L4.94 18.396l-.81.118-.443-.376.046-.659.21-.21 1.74-1.226-.025.046Z"
    />
  </svg>
);

const ChatGPTMark = ({ size = 36 }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
    <path
      fill="#10A37F"
      d="M22.2819 9.8211a5.9847 5.9847 0 0 0-.5157-4.9108 6.0462 6.0462 0 0 0-6.5098-2.9A6.0651 6.0651 0 0 0 4.9807 4.1818a5.9847 5.9847 0 0 0-3.9977 2.9 6.0462 6.0462 0 0 0 .7427 7.0966 5.98 5.98 0 0 0 .511 4.9107 6.051 6.051 0 0 0 6.5146 2.9001A5.9847 5.9847 0 0 0 13.2599 24a6.0557 6.0557 0 0 0 5.7718-4.2058 5.9894 5.9894 0 0 0 3.9977-2.9001 6.0557 6.0557 0 0 0-.7475-7.0729zm-9.022 12.6081a4.4755 4.4755 0 0 1-2.8764-1.0408l.1419-.0804 4.7783-2.7582a.7948.7948 0 0 0 .3927-.6813v-6.7369l2.02 1.1686a.071.071 0 0 1 .038.052v5.5826a4.504 4.504 0 0 1-4.4945 4.4944zm-9.6607-4.1254a4.4708 4.4708 0 0 1-.5346-3.0137l.142.0852 4.783 2.7582a.7712.7712 0 0 0 .7806 0l5.8428-3.3685v2.3324a.0804.0804 0 0 1-.0332.0615L9.74 19.9502a4.4992 4.4992 0 0 1-6.1408-1.6464zM2.3408 7.8956a4.485 4.485 0 0 1 2.3655-1.9728V11.6a.7664.7664 0 0 0 .3879.6765l5.8144 3.3543-2.0201 1.1685a.0757.0757 0 0 1-.071 0l-4.8303-2.7865A4.504 4.504 0 0 1 2.3408 7.872zm16.5963 3.8558L13.1038 8.364 15.1192 7.2a.0757.0757 0 0 1 .071 0l4.8303 2.7913a4.4944 4.4944 0 0 1-.6765 8.1042v-5.6772a.79.79 0 0 0-.407-.667zm2.0107-3.0231l-.142-.0852-4.7735-2.7818a.7759.7759 0 0 0-.7854 0L9.409 9.2297V6.8974a.0662.0662 0 0 1 .0284-.0615l4.8303-2.7866a4.4992 4.4992 0 0 1 6.6802 4.66zM8.3065 12.863l-2.02-1.1638a.0804.0804 0 0 1-.038-.0567V6.0742a4.4992 4.4992 0 0 1 7.3757-3.4537l-.142.0805L8.704 5.459a.7948.7948 0 0 0-.3927.6813zm1.0976-2.3654l2.602-1.4998 2.6069 1.4998v2.9994l-2.5974 1.4997-2.6067-1.4997Z"
    />
  </svg>
);

const CustomMark = ({ size = 36 }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
    <circle cx="12" cy="12" r="10" fill="none" stroke="#7b1fa2" strokeWidth="1.75" />
    <path
      d="M12 7.5v9 M7.5 12h9"
      stroke="#7b1fa2"
      strokeWidth="1.75"
      strokeLinecap="round"
    />
  </svg>
);

const APIMark = ({ size = 36 }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
    <path
      d="M8 6l-6 6 6 6 M16 6l6 6-6 6 M14 4l-4 16"
      fill="none"
      stroke="#0a84ff"
      strokeWidth="1.75"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);

const MCPMark = ({ size = 36 }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
    <circle cx="12" cy="12" r="3" fill="none" stroke="#16a34a" strokeWidth="1.75" />
    <circle cx="5" cy="6" r="2" fill="none" stroke="#16a34a" strokeWidth="1.75" />
    <circle cx="5" cy="18" r="2" fill="none" stroke="#16a34a" strokeWidth="1.75" />
    <circle cx="19" cy="12" r="2" fill="none" stroke="#16a34a" strokeWidth="1.75" />
    <path
      d="M7 7l3 3 M7 17l3-3 M17 12h-2"
      stroke="#16a34a"
      strokeWidth="1.75"
      strokeLinecap="round"
    />
  </svg>
);

// The "?" beside a field label. A mouse opens it on hover; touch toggles it on
// tap (09-24, measured: a tap's emulated mouseover opened it and the tap's own
// click shut it again, so on phones it never showed). The bubble sits beside
// the "?" when it fits, else below it, kept on screen: the old fixed left:22
// ran ~100px off a 375px phone.
const InfoTip = ({ children }) => {
  const [open, setOpen] = useState(false);
  const [pos, setPos] = useState({ left: 22, top: -4, width: 304 });
  const ref = useRef(null);
  const pointerRef = useRef('mouse');

  const show = () => {
    const r = ref.current.getBoundingClientRect();
    const vw = window.innerWidth;
    const width = Math.min(304, vw - 24);
    setPos(r.left + 22 + width <= vw - 12
      ? { left: 22, top: -4, width }
      : { left: Math.max(12, vw - 12 - width) - r.left, top: 22, width });
    setOpen(true);
  };

  // Touch has no mouseleave: a tap anywhere else closes it.
  useEffect(() => {
    if (!open) return undefined;
    const closeOutside = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener('pointerdown', closeOutside);
    return () => document.removeEventListener('pointerdown', closeOutside);
  }, [open]);

  return (
    <span
      ref={ref}
      onPointerEnter={(e) => { if (e.pointerType === 'mouse') show(); }}
      onPointerLeave={(e) => { if (e.pointerType === 'mouse') setOpen(false); }}
      onPointerDown={(e) => { pointerRef.current = e.pointerType; }}
      onClick={(e) => {
        e.preventDefault();
        if (open && pointerRef.current !== 'mouse') setOpen(false);
        else show();
      }}
      style={{ position: 'relative', display: 'inline-block', marginLeft: 6, verticalAlign: 'middle' }}
    >
      <span
        aria-label="More info"
        style={{
          display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
          width: 16, height: 16, borderRadius: '50%',
          background: '#e5e7eb', color: '#6b7280',
          fontSize: 11, fontWeight: 700, cursor: 'help', userSelect: 'none',
        }}
      >?</span>
      {open && (
        <span
          role="tooltip"
          style={{
            position: 'absolute', left: pos.left, top: pos.top, zIndex: 20,
            background: '#1f2937', color: '#f9fafb',
            padding: '8px 12px', borderRadius: 6,
            fontSize: 12, lineHeight: 1.5, fontWeight: 400,
            width: pos.width, boxSizing: 'border-box', whiteSpace: 'normal',
            boxShadow: '0 4px 14px rgba(0,0,0,0.18)',
          }}
        >{children}</span>
      )}
    </span>
  );
};

// 🔒 09-24: this browser's seat for a space (the same two keys GameStepPage
// and chatGameApi read), as invite body fields. A PRIVATE ported room only
// takes invites from a seat holder; {} when there is no stored seat.
const seatCredsFor = (gameId) => {
  try {
    const playerId = localStorage.getItem(`humanPlayerId_${gameId}`);
    const secret = playerId ? localStorage.getItem(`player_secret_${playerId}`) : null;
    return playerId && secret ? { player_id: playerId, player_secret: secret } : {};
  } catch { return {}; }
};

const AgentImportPage = () => {
  const navigate = useNavigate();
  const [step, setStep] = useState(0);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [result, setResult] = useState(null);

  // Form state
  const [source, setSource] = useState(null); // 'claude' | 'chatgpt' | 'custom'
  const [name, setName] = useState('');
  // 🤝 What the chibi calls its person (Nina 08-14). Optional on every
  // source; passport v4.6's PERSON line prefills it. Blank = the chibi says
  // "my human" — the anon site handle must NEVER be presented as the
  // creator's name.
  const [ownerCallName, setOwnerCallName] = useState('');
  // 🧑‍🦊 W2b D11 (PORTED_HOME_HANDOFF 09-25): optional, never guessed from a name
  const [ownerPronouns, setOwnerPronouns] = useState('');
  // 🎵🕐 Port persona fields (Nina 08-17): the song that plays in their space
  // (create-instant music_url + agent metadata) and the "usually up to" lines
  // that become the agent's personal statuses (spaceLife blend). The lines
  // field is wizard-REQUIRED; the API keeps both optional on purpose — agents
  // registering via agents.md must never be forced through wizard questions.
  const [themeSong, setThemeSong] = useState('');
  const [usuallyUpTo, setUsuallyUpTo] = useState('');
  // Soft-gate confirm state: true after the first Create click with the
  // usually-up-to field empty (the "are you sure?" moment). Typing resets it.
  const [softConfirm, setSoftConfirm] = useState(false);
  // Once the user touches the field, prefill NEVER writes it again — a
  // deliberately cleared name must stay cleared (the privacy action the
  // field's copy invites), which a blank-means-untouched check can't tell.
  const ownerCallTouchedRef = useRef(false);
  const [description, setDescription] = useState('');
  const [appearance, setAppearance] = useState('');
  const [systemPrompt, setSystemPrompt] = useState('');
  const [gptConfig, setGptConfig] = useState('');
  const [conversationHistory, setConversationHistory] = useState('');
  const [knowledgeFiles, setKnowledgeFiles] = useState([]);
  const [preferredModel, setPreferredModel] = useState('anthropic/claude-haiku-4.5');
  const [tags, setTags] = useState([]);
  // Free-text tag entry (Nina 08-13). Tags are marketplace browse filters
  // only — never fed to the model — so arbitrary strings are safe.
  const [customTag, setCustomTag] = useState('');
  // Share toggle (Nina 08-18, supersedes the 08-13 public-by-default ruling):
  // OFF by default — the wizard's choice only. The register API's own default
  // stays public (agents.md registrants are unaffected), and the backend
  // marketplace listing/leaderboards/ticker already filter is_public=false
  // server-side (verified 08-18). The toggle promises LISTING only, on
  // purpose: profile-by-id and invite-by-id do not check is_public today.
  const [isPublic, setIsPublic] = useState(false);
  const [agentType, setAgentType] = useState('imported'); // 'imported' | 'connected'
  // 🛫 D9 (Nina 08-14): explicit send-on-expedition from the success screen.
  // null = untouched, 'queuing' = in flight, string = result copy.
  const [expeditionNote, setExpeditionNote] = useState(null);
  // Success-screen queue pulse (SEAMLESS_TRAVEL items 4+6): a 30s poll of
  // the public queue read turns the static "you're in line" copy into live
  // position + soft ETA, and surfaces the watch link the moment the live
  // walk is THIS agent. Garnish by contract — a failed poll leaves the
  // static copy standing.
  const [queuePulse, setQueuePulse] = useState(null);
  useEffect(() => {
    if (!result || !result.agent_id) return undefined;
    let alive = true;
    const pull = async () => {
      try {
        const res = await fetch(`${API_BASE_URL}/api/expeditionQueue/public`);
        const d = await res.json();
        if (!alive || !d || !d.success) return;
        const idx = (d.queue || []).findIndex((q) => q.agent_id === result.agent_id);
        const etaMin = idx > 0
          ? d.queue.slice(0, idx).reduce((n, q) => n + (q.maiden ? 8 : (q.duration_min || 15)), 0) + (d.live ? 10 : 0)
          : null;
        setQueuePulse({
          position: idx >= 0 ? idx + 1 : null,
          walksAhead: idx >= 0 ? idx : null,
          etaMin,
          liveOther: !!(d.live && d.live.marketplace_agent_id !== result.agent_id),
          liveNow: !!(d.live && d.live.marketplace_agent_id === result.agent_id),
        });
      } catch (e) { /* pulse is garnish */ }
    };
    pull();
    const t = setInterval(pull, 30000);
    return () => { alive = false; clearInterval(t); };
  }, [result]);
  // Soft ETA copy (item 4): honest ranges, no fake precision.
  const pulseEtaCopy = (pulse) => {
    if (!pulse || pulse.walksAhead == null) return '';
    if (pulse.walksAhead === 0) return pulse.liveOther ? ', up next after the current walk' : ', up next!';
    return pulse.etaMin < 60 ? `, roughly ${pulse.etaMin} min away` : ', probably later today';
  };
  const [webhookUrl, setWebhookUrl] = useState('');

  // Passport flow state. previewedRaw tracks which paste the preview belongs
  // to — edits after a check mark the preview stale and re-gate the import.
  const [rawPassport, setRawPassport] = useState('');
  const [passportPreview, setPassportPreview] = useState(null);
  const [previewedRaw, setPreviewedRaw] = useState(null);
  const [passportChecking, setPassportChecking] = useState(false);
  const [passportError, setPassportError] = useState(null);
  const [inviteCopied, setInviteCopied] = useState(false);
  // 📧 Owner email (seamless-travel item 1). The column, the register/import
  // params and the SES templates have all existed for weeks — the wizard was
  // simply never asking, so EVERY agent minted so far has owner_email NULL
  // and no recovery path (the first organic user, 08-20, is already in that
  // state). Capture cannot be retroactive: an agent minted without it can
  // never be emailed, even after SES is armed. Optional, never a gate — the
  // register API is the agents.md door and a wizard question must not 400 an
  // agent.
  const [ownerEmail, setOwnerEmail] = useState('');
  // A wizard field is not inside a <form> and is submitted by a button
  // onClick, so type="email" NEVER validates here — the browser only runs
  // constraint validation on form submit. Without this a typo'd address is
  // stored verbatim and is indistinguishable from a real one later, which
  // matters precisely because it is the ONLY contact we will ever have for
  // this agent. Mirrors the backend's normalizer in agentApi.registerAgent.
  const ownerEmailBad = !!ownerEmail.trim()
    && !/^[^\s@,;<>"']+@[^\s@,;<>"']+\.[^\s@,;<>"']{2,}$/.test(ownerEmail.trim());
  // 🎤 "no project instructions" helper: collapsed by default so it never
  // competes with the paste box for users who DO have instructions.
  const [instructionsHelpOpen, setInstructionsHelpOpen] = useState(false);
  const [interviewCopied, setInterviewCopied] = useState(false);
  // 🪪 Import-card flow (Nina 08-18): the context step opens on a copy-paste
  // interview — the user's AI answers one card anywhere, the paste prefills
  // the wizard, and every value stays editable in the normal fields. The
  // classic form is one click away (manualMode). Client-only by design: the
  // register API never sees the card (wizard gating never gates the API).
  const [manualMode, setManualMode] = useState(false);
  const [rawCard, setRawCard] = useState('');
  const [cardError, setCardError] = useState(null);
  const [cardNotice, setCardNotice] = useState(null); // { filled: [], kept: [] }
  // The passport twin of cardNotice. Folding the check into Next means the
  // preview box on the context step is never seen on the happy path (Next
  // advances past it), so the review-and-edit moment moves to the Name step
  // — same fix Nina's ruling 4 forced in the card lane.
  const [passportNotice, setPassportNotice] = useState(null); // { filled, kept }
  // The Name step's "what the passport said" disclosure. Collapsed by
  // default: the notice above it carries the summary, and this is the
  // full-detail escape for anyone who wants it. Never derived from a
  // live-typed value (the round-1/2/3 lesson) — it is user-toggled only.
  const [passportDetailOpen, setPassportDetailOpen] = useState(false);
  const [cardCopied, setCardCopied] = useState(false);
  // Extras (history + knowledge) collapsed on the card panel so the happy
  // path reads copy → paste → Next (Nina 08-20). Same state as the classic
  // form's sections; filling them in either place is the same submit.
  const [extrasOpen, setExtrasOpen] = useState(false);
  // The paste Next last applied — an unchanged paste just advances instead
  // of re-parsing (mirrors the passport lane's previewedRaw staleness check).
  const appliedCardRawRef = useRef(null);
  // Per-field values the last paste applied: a corrected re-paste overwrites
  // fields the user never touched (current value still equals what the card
  // set) instead of silently no-oping; a user edit always wins (the passport
  // lane's still-equals-previous-parse guard, applied card-wide).
  const appliedFieldsRef = useRef({});
  // The Name step's instructions disclosure (card path only). Auto-opens
  // via the effect below when the card had no INSTRUCTIONS answer — never
  // computed from live systemPrompt at render time, which would collapse
  // the box mid-keystroke.
  const [cardInstrOpen, setCardInstrOpen] = useState(false);
  // Which chatgpt manual layout shows: instructions-first (card fill landed
  // there, or instructions already written) vs the classic config-first
  // form. Set ONLY at discrete transitions (card fill, the manual link, a
  // source pick) — NEVER derived from live typing. Two review rounds proved
  // live-derived visibility is unfixable: branching unmounts the focused
  // box (round 1), display:none still blurs it (round 2), and a blur-
  // deferred hide eats the next click via layout shift (round 3).
  const [gptInstrFirst, setGptInstrFirst] = useState(false);
  useEffect(() => {
    // Only ever OPENS (setting true when already true is a no-op), so typing
    // in the box can never flip it shut under the cursor.
    if (cardNotice && !systemPrompt.trim()) setCardInstrOpen(true);
  }, [cardNotice, systemPrompt]);
  // Every step change starts at the top. Without this the browser keeps the
  // scroll offset from a long context step, so the notice/review box we just
  // moved to the TOP of the Name step can land above the viewport — the
  // review moment would be missed for the second time, by a different cause.
  // Safe: `step` only changes at discrete navigation moments.
  useEffect(() => { window.scrollTo({ top: 0, behavior: 'smooth' }); }, [step]);
  // OUR SONG comes back as title + artist only (the template bans links: a
  // model asked for a URL invents one). Rendered as a hint + YouTube search
  // link beside the real song field; music_url stays human-pasted.
  const [songHint, setSongHint] = useState('');
  // Custom-source knowledge uploads are post-register and best-effort; a
  // partial failure shows here on the success screen (the agent IS made).
  const [knowledgeNote, setKnowledgeNote] = useState(null);
  const [apiKeyCopied, setApiKeyCopied] = useState(false);
  const [agentIdCopied, setAgentIdCopied] = useState(false);
  // 🔑 Save-gate (Nina 08-14, audit fix pass): the key is shown ONCE and
  // there is no recovery (seamless-travel item 1) — the What's-next buttons
  // stay locked until the user copies, downloads, or explicitly confirms.
  const [keySaved, setKeySaved] = useState(false);
  // Inline errors (were native alert()s): register submit + auto-create-space.
  const [submitError, setSubmitError] = useState(null);
  const [createSpaceError, setCreateSpaceError] = useState(null);
  // Space generation is the slow option (theme gen rides create-instant) and
  // had NO busy state — Nina clicked and couldn't tell anything was
  // happening (08-14). True while the space is being built; the four main
  // what's-next actions disable during it (passive links stay live).
  const [creatingSpace, setCreatingSpace] = useState(false);
  // 🔒 Share or private, for the NEW home space (Nina 09-24: "an option for
  // private/unlisted imported spaces, like the agents"). Checked = today's
  // behavior (listed on Explore). Unchecked sends is_shared:false, and an
  // unshared ported space is PRIVATE server-side (utils/privateRoomGate):
  // only seats already in it get in, so the link opens for nobody else and
  // the owner's seat lives in THIS browser (no owner seat recovery yet).
  const [shareSpace, setShareSpace] = useState(true);
  // 🚪 Launch chooser (Nina 08-28): the success screen shows ONE clear CTA;
  // clicking it opens this popup with the two doors — new space vs join a
  // space you already have a link to. Replaces the old pair of equal-weight
  // buttons (one of which dead-ended on /marketplace).
  const [showLaunchModal, setShowLaunchModal] = useState(false);
  // 🚪🔗 The space the user came FROM (quickstart forwards ?space=<id> from
  // the Add Agent box, utils/carriedSpace.js). The success screen then leads
  // with "send it back to your space" and the join door comes prefilled.
  const [carriedSpaceId] = useState(() => carriedSpaceFromSearch(window.location.search));
  const [carriedJoin, setCarriedJoin] = useState(null); // null | 'busy' | { ok, error? }
  const [joinInput, setJoinInput] = useState(() => carriedSpaceFromSearch(window.location.search) || '');
  const [joinError, setJoinError] = useState(null);
  const [joinBusy, setJoinBusy] = useState(false);
  const [joinDone, setJoinDone] = useState(null); // { gameId } on success
  // Hatch greet (§4.3 ~15s payoff): after a passport import the backend
  // stashes the HATCH LINE for off-air TTS; the success screen plays his
  // first words. 'idle' | 'loading' | 'played' | 'failed'.
  const [greetState, setGreetState] = useState('idle');

  // ── Refresh mode (Nina 08-12 ruling ④: full wizard reuse) ──────────────
  // /agents/import?refresh=<agentId> reruns the passport wizard against an
  // EXISTING chibi: owner key from the dashboard's stored key ring, preview
  // shows a before/after dossier diff, import updates in place — no new
  // agent, no new api_key. Without a stored key the flow can't auth; the
  // screen points back to the dashboard instead of half-working.
  const [refreshId] = useState(() => new URLSearchParams(window.location.search).get('refresh'));
  const [refreshKey] = useState(() => {
    const id = new URLSearchParams(window.location.search).get('refresh');
    if (!id) return null;
    try { return JSON.parse(localStorage.getItem('spaces_agent_api_keys') || '{}')[id] || null; } catch { return null; }
  });
  // ⏸️ Refresh mode is a PASSPORT surface, so it dies with the door (Nina
  // 08-14: "deactivate passport refresh mode and anything else for passport
  // for now"). Gated here at the single derived flag rather than at each of
  // its ~20 read sites: every prefill, diff, auth-header and copy branch keys
  // off isRefresh, so switching it off closes all of them at once and a
  // hand-typed ?refresh=<id> URL lands on the ordinary source picker.
  const isRefresh = SHOW_CHIBI_PASSPORT_DOOR && !!(refreshId && refreshKey);
  const [currentAgent, setCurrentAgent] = useState(null); // prefill + diff BEFORE side
  const [regenAvatar, setRegenAvatar] = useState(false);
  // Did the live row's visibility actually load? If the prefill fetch failed
  // we must NOT send is_public at all on refresh — sending the form default
  // (true) would flip a private chibi public behind the owner's back.
  const [visibilityLoaded, setVisibilityLoaded] = useState(false);

  useEffect(() => {
    if (!isRefresh) return;
    setSource('passport');
    setStep(1);
    (async () => {
      try {
        const res = await fetch(`${API_BASE_URL}/api/agents/me`, { headers: { 'Authorization': `Bearer ${refreshKey}` } });
        const data = await res.json();
        if (data.success && data.agent) {
          setCurrentAgent(data.agent);
          setName(data.agent.name || '');
          // Prefill visibility from the LIVE row — a private chibi must not
          // flip public just because the form's default is true.
          setIsPublic(data.agent.is_public !== false);
          setVisibilityLoaded(true);
          // Prefill the stored call-name too (refresh mode showed blank even
          // when one existed) — same touched guard as the passport prefill.
          try {
            const meta = typeof data.agent.metadata === 'string'
              ? (JSON.parse(data.agent.metadata || '{}') || {})
              : (data.agent.metadata || {});
            if (meta.owner_call_name && !ownerCallTouchedRef.current) {
              setOwnerCallName(String(meta.owner_call_name).slice(0, 60));
            }
          } catch { /* metadata unreadable: field stays blank */ }
        }
      } catch (err) { /* diff view degrades; the import still auths server-side */ }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isRefresh]);

  // 🛂 Marketplace CHIBI PASSPORT door (Nina 08-14): ?source=passport lands
  // straight on "Your Claude's passport", skipping the source picker. Mirrors
  // the picker's click, share-default included; refresh mode wins — it
  // already forces passport + step 1 with its own prefills.
  useEffect(() => {
    if (isRefresh) return;
    if (!SHOW_CHIBI_PASSPORT_DOOR) return;   // ⏸️ door paused — see the flag
    if (new URLSearchParams(window.location.search).get('source') === 'passport') {
      setSource('passport');
      // Share defaults OFF, same as the picker (Nina 08-18). This line used to
      // set TRUE and its comment still claimed it "mirrors the picker's click"
      // — it mirrored the SUPERSEDED 08-13 picker, so the marketplace hero
      // door published every chibi it minted while the source card kept them
      // private. Same wizard, opposite privacy outcomes, and the marketing
      // door produced the public one.
      setIsPublic(false);
      setStep(1);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isRefresh]);

  const playHatchGreet = async (agentId) => {
    setGreetState('loading');
    try {
      const res = await fetch(`${EXPLORER_API_BASE_URL}/explorer/tts?greet_agent=${encodeURIComponent(agentId)}`);
      const data = await res.json();
      if (!data.ok || !data.audio_b64) throw new Error(data.reason || 'no audio');
      const audio = new Audio(`data:${data.mime || 'audio/wav'};base64,${data.audio_b64}`);
      await audio.play();
      setGreetState('played');
    } catch (err) {
      // Autoplay policies or a TTS hiccup — the moment stays button-shaped.
      setGreetState('failed');
    }
  };

  // Clipboard with the http/old-Safari fallback, shared by every copy
  // affordance on this page.
  const copyToClipboard = async (text) => {
    try {
      await navigator.clipboard.writeText(text);
    } catch {
      // Clipboard API can be unavailable (http, old Safari) — fall back to a
      // hidden textarea + execCommand.
      const ta = document.createElement('textarea');
      ta.value = text;
      document.body.appendChild(ta);
      ta.select();
      document.execCommand('copy');
      document.body.removeChild(ta);
    }
  };

  const copyInvitation = async () => {
    await copyToClipboard(PASSPORT_INVITATION);
    setInviteCopied(true);
    setTimeout(() => setInviteCopied(false), 2000);
  };

  const copyInterview = async () => {
    await copyToClipboard(INSTRUCTIONS_INTERVIEW);
    setInterviewCopied(true);
    setTimeout(() => setInterviewCopied(false), 2000);
  };

  const copyCard = async () => {
    await copyToClipboard(IMPORT_CARD_INTERVIEW);
    setCardCopied(true);
    setTimeout(() => setCardCopied(false), 2000);
  };

  // Fill the wizard from the pasted card. Runs from Next (Nina 08-20:
  // filling is part of Next, not a separate chore — the passport lane's
  // 08-14 ruling, applied here). Overwrite rule: a field fills when it is
  // empty OR still exactly what the LAST paste set (the user never touched
  // it) — so a corrected re-paste actually updates, instead of the old
  // empty-only silent no-op (review fix 08-20). A user edit always wins and
  // is reported as kept. On success the Name step opens prefilled with the
  // notice at its top — that IS the review-and-edit moment. Returns false
  // on a parse error so Next stays on this step with the error box.
  const applyCardFill = () => {
    setCardError(null);
    const parsed = parseImportCard(rawCard);
    if (!parsed.ok) { setCardError(parsed.error); return false; }
    const f = parsed.fields;
    const filled = [];
    const kept = [];
    const prev = appliedFieldsRef.current || {};
    const next = {};
    const put = (label, key, value, current, setter) => {
      if (!value) return;
      const cur = (current || '').trim();
      if (cur && cur !== (prev[key] || '').trim()) { kept.push(label); return; }
      setter(value);
      next[key] = value;
      if (value.trim() !== cur) filled.push(label);
    };
    put('Name', 'name', (f.name || '').slice(0, 100), name, setName);
    put('Bio', 'bio', f.bio, description, setDescription);
    // Same touched guard as the passport prefill — a deliberately cleared
    // or edited call-name stays (and is reported kept, like put fields);
    // an untouched one tracks the paste.
    if (f.callsYou) {
      const v = f.callsYou.slice(0, 60);
      if (!ownerCallTouchedRef.current) {
        if (v !== ownerCallName) { setOwnerCallName(v); filled.push('What they call you'); }
      } else if (v !== ownerCallName) {
        kept.push('What they call you');
      }
    }
    put('Look', 'look', f.look, appearance, setAppearance);
    put('Usually up to', 'usuallyUpTo', f.usuallyUpTo, usuallyUpTo, (v) => { setUsuallyUpTo(v); setSoftConfirm(false); });
    put('Instructions', 'instructions', f.instructions, systemPrompt, setSystemPrompt);
    // The hint tracks the LATEST paste both ways — a card corrected to SKIP
    // clears an invented pick (the hint is not user-editable, so a stale one
    // would have no dismiss). Only REPORT the pick when the hint actually
    // renders (it hides once a real song URL is pasted).
    if (f.song && f.song !== songHint) {
      setSongHint(f.song);
      if (!themeSong.trim()) filled.push('Song pick');
    } else if (!f.song && songHint) { setSongHint(''); }
    // Kept fields keep their PREVIOUS applied value on record, so a user
    // edit stays recognized as an edit across any number of re-pastes.
    appliedFieldsRef.current = { ...prev, ...next };
    // The chatgpt manual layout follows where instructions LANDED — set at
    // this discrete moment, never from live typing (see gptInstrFirst).
    const instrAfter = next.instructions !== undefined ? next.instructions : systemPrompt;
    setGptInstrFirst(!!(instrAfter || '').trim());
    setCardNotice({ filled, kept });
    appliedCardRawRef.current = rawCard;
    return true;
  };

  // Fill the wizard from a parsed visa — the passport twin of applyCardFill,
  // deliberately sharing appliedFieldsRef and the same put() rule so BOTH
  // lanes behave identically: a field fills when it is empty or still exactly
  // what the last paste set, a real user edit always wins and is reported as
  // kept, and a corrected re-paste actually updates instead of no-oping.
  // Called only from checkPassport, which Next awaits.
  const applyPassportFill = (d) => {
    const filled = [];
    const kept = [];
    const prev = appliedFieldsRef.current || {};
    const next = {};
    const put = (label, key, value, current, setter) => {
      if (!value) return;
      const cur = (current || '').trim();
      if (cur && cur !== (prev[key] || '').trim()) { kept.push(label); return; }
      setter(value);
      next[key] = value;
      if (value.trim() !== cur) filled.push(label);
    };
    put('Name', 'name', (d.name || '').slice(0, 100), name, setName);
    // v4.7 UP TO → the status lines under their name. Before this field
    // existed the visa had no answer for "what are they usually up to", so
    // the review step's red soft-gate ask fired for EVERY passport user.
    put('Usually up to', 'usuallyUpTo', (d.upTo || []).join('\n'), usuallyUpTo,
      (v) => { setUsuallyUpTo(v); setSoftConfirm(false); });
    // PERSON: the touched-flag guard, not a value compare — a deliberately
    // CLEARED call-name must stay cleared, and blank-means-untouched cannot
    // tell the difference (same reasoning as the card lane).
    if (d.person) {
      const v = String(d.person).slice(0, 60);
      if (!ownerCallTouchedRef.current) {
        if (v !== ownerCallName) { setOwnerCallName(v); filled.push('What your Claude calls you'); }
      } else if (v !== ownerCallName) {
        kept.push('What your Claude calls you');
      }
    }
    // Kept fields keep their PREVIOUS applied value on record, so an edit
    // stays recognized as an edit across any number of re-pastes.
    appliedFieldsRef.current = { ...prev, ...next };
    // LOOK is NOT prefilled into Appearance on purpose: blank means "keep the
    // LOOK line", which the server maps (passportParser.toImportParams). We
    // only report it so the notice can say it landed.
    setPassportNotice({ filled, kept, hadLook: !!d.look });
  };

  // Returns true when the paste parsed clean — the Next button awaits this
  // (Nina 08-14: checking is part of Next, not a separate chore) and only
  // advances on success. Sole caller is leaveContextStepForward, which both
  // Next and the progress dots go through; the standalone "Check passport"
  // button this used to share was deleted 08-20.
  const checkPassport = async () => {
    setPassportChecking(true);
    setPassportError(null);
    try {
      const response = await fetch(`${API_BASE_URL}/api/agents/import/claude`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...(isRefresh ? { 'Authorization': `Bearer ${refreshKey}` } : {}) },
        body: JSON.stringify({ raw_passport: rawPassport, parse_only: true, ...(isRefresh ? { agent_id: refreshId } : {}) }),
      });
      const data = await response.json();
      if (data.success) {
        setPassportPreview(data);
        setPreviewedRaw(rawPassport);
        applyPassportFill(data.dossier || {});
        return true;
      }
      setPassportPreview(null);
      setPreviewedRaw(null);
      setPassportError(data.error || 'Could not read that passport.');
      return false;
    } catch (err) {
      setPassportError('Could not reach the hatchery: ' + err.message);
      return false;
    } finally {
      setPassportChecking(false);
    }
  };

  // Leaving the context step FORWARD — by Next or by a progress dot — runs
  // the same gates: the passport check and the card fill. The dots used to
  // setStep() bare, which stranded a pasted card unapplied and defeated the
  // corrected-re-paste path (verify-round finding 08-20). Returns false when
  // the caller should stay put (a parse error is showing).
  const leaveContextStepForward = async () => {
    // Passport: Next runs the check itself when the paste is unchecked or
    // stale (Nina 08-14) — advance only on a clean parse. An empty paste
    // still browses forward (the review step's hard gate catches it).
    if (source === 'passport' && rawPassport.trim()
      && (!passportPreview || rawPassport !== previewedRaw)) {
      return await checkPassport();
    }
    // Card (every non-passport source): fill from the paste (Nina 08-20,
    // the same ruling). A parse error keeps you here with the error box; an
    // empty paste browses forward; an unchanged paste is already applied.
    if (source !== 'passport' && !manualMode
      && rawCard.trim() && rawCard !== appliedCardRawRef.current) {
      return applyCardFill();
    }
    return true;
  };

  const handleAddKnowledgeFile = () => {
    setKnowledgeFiles([...knowledgeFiles, { filename: '', content: '' }]);
  };

  // .txt/.md picker (D4). f.text() promise form per the AdminPage caption
  // uploader idiom; input value reset so re-picking the same file re-fires.
  const handleKnowledgeUpload = async (e) => {
    const picked = Array.from(e.target.files || []);
    e.target.value = '';
    const loaded = [];
    for (const f of picked) {
      try {
        const text = await f.text();
        if (text && text.trim()) loaded.push({ filename: f.name, content: text.slice(0, KNOWLEDGE_FILE_MAX) });
      } catch (err) { /* unreadable file — skip it, keep the rest */ }
    }
    if (loaded.length) setKnowledgeFiles(prev => [...prev, ...loaded]);
  };

  // ONE mapper for every submit payload (claude + chatgpt) so the cap can't
  // drift between the two branches.
  const mapKnowledgeFiles = () => knowledgeFiles
    .filter(f => f.filename && f.content)
    .map(f => ({ filename: f.filename, content: f.content.slice(0, KNOWLEDGE_FILE_MAX) }));

  // chatgpt: which of the two context fields ships. Mirrors what the manual
  // view SHOWS — the instructions-first layout (or no pasted config) ships
  // instructions; the classic layout with a pasted config ships the config.
  // Used by the submit body AND the review row so they can never disagree
  // (the audit's systemPrompt-vs-gptConfig mismatch, closed for real).
  const gptShipsInstructions = !!systemPrompt.trim() && (gptInstrFirst || !gptConfig.trim());

  const handleSubmit = async () => {
    setSubmitError(null);
    setIsSubmitting(true);
    try {
      let endpoint, body;

      if (source === 'passport') {
        // Server re-parses the raw paste and imports the CANONICAL dossier
        // text; explicit fields here override parsed defaults (show-then-edit).
        // undefined values drop out of JSON.stringify, so blank optional
        // overrides leave the parser's defaults (LOOK → appearance, etc.).
        endpoint = `${API_BASE_URL}/api/agents/import/claude`;
        body = {
          raw_passport: rawPassport,
          name: name || undefined,
          description: description || undefined,
          appearance: appearance || undefined,
          // Always a string — '' is an EXPLICIT clear the backend honors
          // (dropping the key would resurrect the visa's PERSON default).
          owner_call_name: ownerCallName.trim(),
          preferred_model: PASSPORT_MODEL,
          // On refresh, send visibility only when we KNOW the live value
          // (visibilityLoaded) — a failed prefill must not flip a private
          // chibi public. New imports always send (always public now that
          // the visibility selector is gone, Nina 08-13).
          ...(!isRefresh || visibilityLoaded ? { is_public: isPublic } : {}),
          ...(tags.length ? { tags: ['chibi-passport', ...tags] } : {}),
          // Refresh mode: update-in-place controls (backend 403s unless the
          // Bearer key below matches this agent).
          ...(isRefresh ? { agent_id: refreshId, ...(regenAvatar ? { regenerate_avatar: true } : {}) } : {}),
          theme_song: themeSong.trim() || undefined,
          // Always a string — '' is an EXPLICIT clear, same rule as
          // owner_call_name above. Dropping the key would let the visa's UP TO
          // lines (passportParser.toImportParams) resurrect a list the user
          // deliberately deleted, and they would then run as statuses under
          // the chibi's name forever. Only the passport branch needs this:
          // no other lane has a server-side default to beat.
          declared_activities: usuallyUpTo.trim(),
          owner_email: ownerEmail.trim() || undefined,
        };
      } else if (source === 'claude') {
        endpoint = `${API_BASE_URL}/api/agents/import/claude`;
        body = {
          name,
          description,
          appearance: appearance || undefined,
          // Always a string — '' is an EXPLICIT clear the backend honors
          // (dropping the key would resurrect the visa's PERSON default).
          owner_call_name: ownerCallName.trim(),
          project_instructions: systemPrompt,
          conversation_excerpts: conversationHistory
            ? conversationHistory.split('\n---\n').map(s => s.trim()).filter(Boolean)
            : [],
          knowledge_files: mapKnowledgeFiles(),
          preferred_model: preferredModel,
          is_public: isPublic,
          tags,
          theme_song: themeSong.trim() || undefined,
          declared_activities: usuallyUpTo.trim() || undefined,
          owner_email: ownerEmail.trim() || undefined,
        };
      } else if (source === 'chatgpt') {
        endpoint = `${API_BASE_URL}/api/agents/import/chatgpt`;
        body = {
          name,
          description,
          appearance: appearance || undefined,
          // Always a string — '' is an EXPLICIT clear the backend honors
          // (dropping the key would resurrect the visa's PERSON default).
          owner_call_name: ownerCallName.trim(),
          // What the manual view shows is what ships (gptShipsInstructions —
          // shared with the review row, audit fix).
          gpt_config: gptShipsInstructions ? undefined : (gptConfig.trim() ? gptConfig : undefined),
          instructions: gptShipsInstructions ? systemPrompt : undefined,
          conversation_excerpts: conversationHistory
            ? conversationHistory.split('\n---\n').map(s => s.trim()).filter(Boolean)
            : [],
          knowledge_files: mapKnowledgeFiles(),
          preferred_model: preferredModel,
          is_public: isPublic,
          tags,
          theme_song: themeSong.trim() || undefined,
          declared_activities: usuallyUpTo.trim() || undefined,
          owner_email: ownerEmail.trim() || undefined,
        };
      } else {
        endpoint = `${API_BASE_URL}/api/agents/register`;
        body = {
          name,
          description,
          appearance: appearance || undefined,
          // Always a string — '' is an EXPLICIT clear the backend honors
          // (dropping the key would resurrect the visa's PERSON default).
          owner_call_name: ownerCallName.trim(),
          system_prompt: systemPrompt,
          conversation_history: conversationHistory
            ? conversationHistory.split('\n---\n').map(s => s.trim()).filter(Boolean)
            : [],
          agent_type: agentType,
          source_platform: 'custom',
          preferred_model: preferredModel,
          webhook_url: agentType === 'connected' ? webhookUrl : undefined,
          is_public: isPublic,
          tags,
          theme_song: themeSong.trim() || undefined,
          declared_activities: usuallyUpTo.trim() || undefined,
          owner_email: ownerEmail.trim() || undefined,
        };
      }

      const response = await fetch(endpoint, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...(source === 'passport' && isRefresh ? { 'Authorization': `Bearer ${refreshKey}` } : {}) },
        body: JSON.stringify(body),
      });
      const data = await response.json();

      if (data.success) {
        // Custom parity (Nina 08-18): the register route has no knowledge
        // handling, so files ride the authed upload with the key we just got
        // — same uploadKnowledgeFile + distillation dispatch as the two
        // import paths. Best-effort: the agent is already registered, so a
        // failed file must never read as a failed import.
        if (source === 'custom' && data.api_key) {
          const files = mapKnowledgeFiles();
          let failed = 0;
          for (const f of files) {
            try {
              const up = await fetch(`${API_BASE_URL}/api/agents/me/knowledge`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${data.api_key}` },
                body: JSON.stringify(f),
              });
              if (!up.ok) failed += 1;
            } catch { failed += 1; }
          }
          if (failed > 0) {
            setKnowledgeNote(`${files.length - failed} of ${files.length} knowledge files attached. You can add the rest from your dashboard.`);
          }
        }
        setResult(data);
        // Store API key so the dashboard can find this agent
        if (data.agent_id && data.api_key) {
          saveAgentApiKey(data.agent_id, data.api_key);
        }
      } else {
        setSubmitError(data.error || 'Failed to register agent. Try again in a moment.');
      }
    } catch (err) {
      setSubmitError('Failed to register agent: ' + err.message);
    } finally {
      setIsSubmitting(false);
    }
  };

  const toggleTag = (tag) => {
    setTags(prev => prev.includes(tag) ? prev.filter(t => t !== tag) : [...prev, tag]);
  };

  const addCustomTag = () => {
    const t = customTag.trim().toLowerCase().slice(0, 24);
    setCustomTag('');
    if (!t) return;
    setTags(prev => prev.includes(t) ? prev : [...prev, t]);
  };

  // ── Launch chooser handlers (Nina 08-28) ─────────────
  // New space: the FAST inline path (create-instant auto-builds + themes the
  // space, claims the human seat, invites the home agent, settles in). Lifted
  // verbatim from the old "Create Space with Agent" button so the popup keeps
  // the one-tap build — NOT the slower /custom-space wizard.
  const handleCreateSpace = async () => {
    if (!result) return;
    setCreateSpaceError(null);
    setCreatingSpace(true);
    try {
      // Seed the space from EVERYTHING the import gathered (Nina 08-13: no
      // manual /build hop for fresh ports). scene_description doubles as the
      // explore description AND theme-gen seed. NEVER include systemPrompt/
      // dossier body — project instructions are promised private. Source guard
      // (08-14): a stale passport preview must not season a non-passport agent.
      const dossier = source === 'passport' ? passportPreview?.dossier : null;
      const personaLine = [
        (description || '').trim(),
        dossier?.into ? `Big on ${dossier.into}.` : '',
        dossier?.quirks?.length ? `${dossier.quirks[0]}.` : '',
      ].filter(Boolean).join(' ')
        .replace(/\.\.+/g, '.').slice(0, 500);
      const res = await fetch(`${API_BASE_URL}/chat-game/create-instant`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          name: `${result.name}'s Place`,
          scene_description: personaLine
            ? `${result.name}'s home space. ${personaLine}`
            : `A space where ${result.name} hangs out with humans and other AIs.`,
          objective: 'Chat, share ideas, and have a good time.',
          players: [{ name: ownerCallName.trim() || 'You', is_human: true, character_description: 'A curious human.' }],
          // 🧑‍🦊 Standing identity (08-28): the seat name above can drift
          // (anon rejoin, second device) — this stamp is what the resident's
          // every-turn awareness line reads. Same string on purpose.
          ...(ownerCallName.trim() ? { owner_display_name: ownerCallName.trim() } : {}),
          // 🧑‍🦊 W2b (09-25): D11 the owner's pronouns, only when given, and D4
          // the room's clock = this browser's zone (the server keeps a valid
          // zone with a '/', drops anything else). The ROOM's create body only:
          // never the register API (the agents.md door).
          ...(ownerPronouns ? { owner_pronouns: ownerPronouns } : {}),
          // 🎨 D8: once the place is live for everyone (the switch, never the
          // one-device preview), a new port skips the generated theme.
          ...(SHOW_PORTED_PLACE ? { ported_place: true } : {}),
          ...(() => { try { const tz = Intl.DateTimeFormat().resolvedOptions().timeZone; return tz ? { house_tz: tz } : {}; } catch (e) { return {}; } })(),
          is_shared: shareSpace,
          interests: tags,
          game_type: 'chat',
          home_agent_id: result.agent_id,
          ...(/^https?:\/\/\S+$/i.test(themeSong.trim()) ? { music_url: themeSong.trim() } : {}),
        }),
      });
      const space = await res.json();
      if (!space.game_id) { setCreateSpaceError(space.error || 'Failed to create the space. Try again in a moment.'); return; }
      // Claim the pre-made human seat: GameStepPage needs BOTH keys or it
      // mints a SECOND anon seat (the two-yous bug). All-or-nothing.
      try {
        (space.players || []).forEach(p => {
          if (p.player_secret) localStorage.setItem(`player_secret_${p.id}`, p.player_secret);
        });
        if (space.human_player_id) {
          localStorage.setItem(`humanPlayerId_${space.game_id}`, space.human_player_id);
        }
      } catch { /* private mode: the join flow seats them */ }
      // 🔒 09-24: "Share on Explore" unchecked makes this a PRIVATE room, and
      // a private room only takes invites from a seat holder. The brand-new
      // owner seat rides straight from the create response (not storage, so
      // private browsing can't break the port).
      const ownerSeat = (space.players || []).find(p => p.id === space.human_player_id);
      await fetch(`${API_BASE_URL}/api/agents/${result.agent_id}/invite`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          game_id: space.game_id,
          ...(ownerSeat?.player_secret ? { player_id: ownerSeat.id, player_secret: ownerSeat.player_secret } : {}),
        }),
      });
      try {
        localStorage.setItem('spaces_settle_in', JSON.stringify({
          agent_id: result.agent_id,
          agent_name: result.name || name,
          game_id: space.game_id,
          ts: Date.now(),
        }));
        window.dispatchEvent(new CustomEvent('spaces-settle-in-changed'));
      } catch { /* private mode — guide just won't show */ }
      navigate(`/game-step?gameId=${space.game_id}&loading=true${space.human_player_id ? `&playerId=${space.human_player_id}` : ''}`);
    } catch (err) {
      setCreateSpaceError('Something went wrong: ' + err.message);
    } finally {
      setCreatingSpace(false);
    }
  };

  // Join existing: seat the agent into a space the owner already has a link
  // to. There is NO space picker — the invite API only takes a game_id — so
  // this is paste-a-URL only (the label promises exactly that). Mirrors
  // LaunchAgentModal's invite section, using the canonical spaceIdFromInput.
  // ONE invite call for both doors (the paste-a-link door and the carried
  // space banner). Resolves { ok: true } or { ok: false, error }.
  // 🔒 09-24: either door can point at a PRIVATE room, which only takes
  // invites from a seat holder, so this browser's seat for that space rides
  // along when it has one (seatCredsFor).
  const inviteAgentToSpace = async (gameId) => {
    if (!result) return { ok: false, error: 'No agent yet.' };
    try {
      const res = await fetch(`${API_BASE_URL}/api/agents/${result.agent_id}/invite`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ game_id: gameId, ...seatCredsFor(gameId) }),
      });
      const d = await res.json();
      if (d.success) return { ok: true };
      return { ok: false, error: d.error || 'Could not invite to that space. Check the link and try again.' };
    } catch (err) {
      return { ok: false, error: 'Could not reach the space. Check your connection and try again.' };
    }
  };

  const handleJoinExisting = async () => {
    if (!result) return;
    const gameId = spaceIdFromInput(joinInput);
    // spaceIdFromInput hands back the raw text when it can't find a UUID, so
    // validate the RESULT directly rather than comparing against the input.
    if (!/^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(gameId)) {
      setJoinError("That doesn't look like a space link or ID. Paste the full space URL or its ID.");
      return;
    }
    setJoinError(null);
    setJoinBusy(true);
    const r = await inviteAgentToSpace(gameId);
    if (r.ok) {
      setJoinInput('');
      setJoinDone({ gameId });
    } else {
      setJoinError(r.error);
    }
    setJoinBusy(false);
  };

  // 🚪🔗 Carried space: one click seats the new agent back where the user
  // came from. Same key gate as every other exit on this screen.
  const handleCarriedJoin = async () => {
    if (!carriedSpaceId || carriedJoin === 'busy') return;
    setCarriedJoin('busy');
    const r = await inviteAgentToSpace(carriedSpaceId);
    setCarriedJoin(r);
  };

  // ── Step 0: Choose Source ────────────────────────────
  const renderSourceStep = () => (
    <div style={styles.stepContent}>
      <h2 style={styles.stepTitle}>Where is your agent from?</h2>
      <p style={styles.stepDesc}>Choose the platform your AI agent was built on.</p>
      <div style={styles.sourceGrid}>
        {[
          // ⏸️ Passport card paused — see SHOW_CHIBI_PASSPORT_DOOR. Filtered
          // rather than deleted so restoring is a one-line flip.
          ...(SHOW_CHIBI_PASSPORT_DOOR
            ? [{ key: 'passport', mark: <span style={{ fontSize: 32, lineHeight: 1 }} aria-hidden="true">&#128706;</span>, label: 'Chibi Passport', desc: 'Give your Claude a body' }]
            : []),
          { key: 'claude', mark: <ClaudeMark />, label: 'Claude', desc: 'Import from Claude Projects' },
          { key: 'chatgpt', mark: <ChatGPTMark />, label: 'ChatGPT', desc: 'Import a Custom GPT' },
          { key: 'custom', mark: <CustomMark />, label: 'Other/Custom', desc: 'Any other AI' },
          { key: 'api', mark: <APIMark />, label: 'API', desc: 'Register programmatically', external: '/agents/docs#api' },
          { key: 'mcp', mark: <MCPMark />, label: 'MCP', desc: 'Register via MCP', external: '/agents/docs#mcp' },
        ].map(s => (
          <button
            key={s.key}
            onClick={() => {
              // Refresh mode forces source='passport' — re-picking a source
              // here would rerun setIsPublic(false) and clobber the live
              // row's prefilled visibility (review fix C, 08-12).
              if (isRefresh) return;
              if (s.external) navigate(s.external);
              else {
                setSource(s.key);
                // Share defaults OFF (Nina 08-18, supersedes the 08-13
                // public-by-default ruling). Reset on source flip so a stale
                // value from an abandoned refresh can't leak into a fresh
                // import.
                setIsPublic(false);
                // Card view resets with the source; the paste itself is kept
                // so flipping sources doesn't eat a long reply. The applied
                // marker resets WITH the notice — otherwise the next Next
                // sees an unchanged paste, skips the re-fill, and the Name
                // step renders without the notice or the instructions
                // disclosure while the fill still ships (verify-round
                // finding 08-20). appliedFieldsRef stays, so the re-fill
                // keeps recognizing user edits.
                appliedCardRawRef.current = null;
                setManualMode(false);
                setGptInstrFirst(!!systemPrompt.trim());
                setCardError(null);
                setCardNotice(null);
                setStep(1);
              }
            }}
            style={{ ...styles.sourceCard, ...(source === s.key ? styles.sourceCardActive : {}) }}
          >
            <span style={styles.sourceMark}>{s.mark}</span>
            <span style={styles.sourceLabel}>{s.label}</span>
            <span style={styles.sourceDesc}>{s.desc}</span>
          </button>
        ))}
      </div>
    </div>
  );

  // ── Step 1: Import Context ───────────────────────────
  // 🎤 The "I don't have project instructions" door (Nina 08-14). Collapsed by
  // default: users who already have instructions must not have to read past
  // it. Rides every source that asks for a system prompt, so nobody hits a
  // required textarea with no way to fill it.
  const renderInstructionsHelp = () => (
    <div style={styles.helpPanel}>
      <button
        type="button"
        onClick={() => setInstructionsHelpOpen(o => !o)}
        aria-expanded={instructionsHelpOpen}
        style={styles.helpToggle}
      >
        <span>{instructionsHelpOpen ? '▾' : '▸'}</span>
        <span>Don&rsquo;t have project instructions?</span>
      </button>
      {instructionsHelpOpen && (
        <div style={styles.helpBody}>
          <p style={styles.helpLead}>
            <strong>Interview your AI instead.</strong> Copy this,
            paste it into any chat with your AI, and paste its answer into the
            box above. It writes its own instructions.
          </p>
          <pre style={styles.helpPre}>{INSTRUCTIONS_INTERVIEW}</pre>
          <button
            type="button"
            onClick={copyInterview}
            className="import-chip-button"
            style={{ ...styles.addFileButton, borderColor: '#7C4DFF', color: '#5b32d6' }}
          >
            {interviewCopied ? 'Copied!' : '📋 Copy the interview'}
          </button>
        </div>
      )}
    </div>
  );

  // 🪪 The card-first face of the context step (every non-passport source).
  // Copy the questions, paste the reply, hit Next; or bail to the classic
  // form via the quiet link at the bottom (Nina 08-20: the escape hatch was
  // the FIRST button on the page, inviting a click before the happy path).
  const renderCardPanel = () => (
    <>
      <p style={styles.stepDesc}>
        Copy the questions to your AI, paste its reply back, and hit Next.
        You review everything before anything is created.
      </p>

      <label style={{ ...styles.label, marginTop: 22 }}>1. Copy the questions</label>
      <p style={styles.hint}>
        Paste them into any chat with your AI, wherever it lives. It answers as itself.
      </p>
      <div style={styles.inviteBox}>{IMPORT_CARD_INTERVIEW}</div>
      <button onClick={copyCard} style={{ ...styles.primaryButton, marginTop: 10 }}>
        {cardCopied ? 'Copied!' : '📋 Copy the questions'}
      </button>

      <label style={{ ...styles.label, marginTop: 28 }}>2. Paste the reply</label>
      <p style={styles.hint}>The whole reply is fine. We find the card. Then hit Next and the answers fill in.</p>
      <textarea
        value={rawCard}
        onChange={e => { setRawCard(e.target.value); setCardError(null); }}
        placeholder={'=== IMPORT CARD v1 ===\nNAME: ...'}
        style={styles.textarea}
        rows={8}
      />
      {cardError && (
        <div style={styles.passportErrorBox} role="alert">{cardError}</div>
      )}

      {/* Extras stay collapsed so the happy path reads copy → paste → Next.
          Same state as the classic form's sections, so filling them in
          either place feeds the same submit. */}
      <div style={{ marginTop: 18 }}>
        <button
          type="button"
          onClick={() => setExtrasOpen(o => !o)}
          aria-expanded={extrasOpen}
          style={styles.helpToggle}
        >
          <span>{extrasOpen ? '▾' : '▸'}</span>
          <span>Add extras: conversation history, knowledge files (optional)</span>
        </button>
        {extrasOpen && renderHistoryAndKnowledge()}
      </div>

      <p style={{ ...styles.hint, marginTop: 18 }}>
        Prefer to type it all yourself?{' '}
        <button
          type="button"
          onClick={() => {
            // Entering the classic form is a discrete moment — pick the
            // chatgpt layout HERE, never from live typing (gptInstrFirst).
            setGptInstrFirst(!!systemPrompt.trim());
            setManualMode(true);
          }}
          style={styles.inlineLinkButton}
        >
          Fill everything out manually
        </button>.
        {source === 'claude' && <> Claude Project instructions paste in there too.</>}
      </p>
    </>
  );

  // Shown at the top of the classic form right after a fill, so the person
  // knows what landed and that every field below is editable.
  const renderCardNotice = () => (
    <div style={{ ...styles.previewBox, marginTop: 12 }}>
      <div style={{ fontWeight: 700, marginBottom: 6 }}>&#129706; Answers filled in</div>
      <div style={{ fontSize: 13 }}>
        Filled: {cardNotice.filled.length ? cardNotice.filled.join(' \u00b7 ') : 'nothing new'}.
        {cardNotice.kept.length > 0 && <> Kept your edits to {cardNotice.kept.join(' \u00b7 ')}.</>}
      </div>
      <p style={{ ...styles.hint, marginTop: 8, marginBottom: 0 }}>
        Look over each answer below. Edit anything.
      </p>
    </div>
  );

  // 🛂 The passport face of the context step — the same shape renderCardPanel
  // uses: copy, paste, Next. It has NO action button of its own. The old
  // standalone "Check passport" button is gone (Nina's 08-20 card ruling,
  // applied to the lane that ruling originally came from): the check rides
  // Next via leaveContextStepForward, and the review moment moved to the Name
  // step, where it sits next to the fields it prefilled and can be acted on.
  const renderPassportPanel = () => (
    <>
      <p style={styles.stepDesc}>
        Copy the invitation to your Claude, paste its reply back, and hit Next.
        You review everything before anything is created.
      </p>

      <label style={{ ...styles.label, marginTop: 22 }}>1. Copy the invitation</label>
      <p style={styles.hint}>
        Paste it into your chat with your Claude on claude.ai. It fills out a visa application in its own words.
      </p>
      <div style={styles.inviteBox}>{PASSPORT_INVITATION}</div>
      <button onClick={copyInvitation} style={{ ...styles.primaryButton, marginTop: 10 }}>
        {inviteCopied ? 'Copied!' : '\uD83D\uDCCB Copy the invitation'}
      </button>

      {/* Keeps the * that the card lane does not have: the card's paste is
          optional (an empty one browses forward), but Create HARD-requires a
          read passport, so the marker is telling the truth here. */}
      <label style={{ ...styles.label, marginTop: 28 }}>2. Paste the reply *</label>
      <p style={styles.hint}>
        The whole reply is fine. We find the visa block. Then hit Next and the answers fill in.
      </p>
      <textarea
        value={rawPassport}
        onChange={e => { setRawPassport(e.target.value); setPassportError(null); }}
        placeholder={'=== CHIBI VISA v1 ===\nNAME: ...'}
        style={styles.textarea}
        rows={10}
      />
      {passportError && (
        <div style={styles.passportErrorBox} role="alert">{passportError}</div>
      )}

      {/* The quiet escape, at the BOTTOM (Nina 08-20: the old card lane put
          its escape at the top, where it was the first clickable thing on the
          page and invited a click before the happy path was read). Sends you
          back to the source picker rather than mutating `source` in place —
          nothing to reset, and refresh mode has no picker to return to. */}
      {!isRefresh && (
        <p style={{ ...styles.hint, marginTop: 18 }}>
          No claude.ai handy?{' '}
          {/* Held during an in-flight check, exactly like Back/Next/the dots.
              Without this it was the ONE door still open: escape mid-check to
              step 0, pick another source, and checkPassport's continuation
              then wrote the visa's fields into a DIFFERENT lane and yanked the
              user forward — the round-4 stale-completion bug, re-opened by a
              link that did not exist when that guard was written. */}
          <button
            type="button"
            onClick={() => setStep(0)}
            disabled={passportChecking}
            style={{
              ...styles.inlineLinkButton,
              opacity: passportChecking ? 0.6 : 1,
              cursor: passportChecking ? 'default' : 'pointer',
            }}
          >
            Pick another way in
          </button>.
        </p>
      )}
    </>
  );

  // The passport's review-and-edit moment, rendered at the top of the NAME
  // step. Why it lives here and not on the paste screen: checkPassport writes
  // the preview and Next calls setStep in the same async continuation, so the
  // old context-step preview box was written and never painted on the happy
  // path — its warnings ("no SAMPLE LINES — voice fidelity will suffer") have
  // never been seen by anyone. The refresh diff + regenAvatar checkbox move
  // with it on purpose: they lived inside that same unreachable block, and
  // leaving them behind would make "redraw the portrait" impossible to tick.
  const renderPassportReview = () => (
    <div style={{ ...styles.previewBox, marginTop: 12 }}>
      <div style={styles.previewHeader}>
        <span style={{ fontWeight: 700 }}>&#128706; Passport read</span>
        {/* charCount is the length of the SERVER's canonical rebuild, not of
            the paste — never wire this to the textarea. */}
        <span style={{ color: passportPreview.charCount > PASSPORT_DOSSIER_MAX ? '#c62828' : '#2e7d32', fontSize: 13 }}>
          {passportPreview.charCount.toLocaleString()} / {PASSPORT_DOSSIER_MAX.toLocaleString()} characters
        </span>
      </div>
      {/* Safe against the live-typed-visibility lesson: the textarea lives in
          renderPassportPanel (step 1) and only one step renders at a time, so
          rawPassport cannot change while this box is on screen. */}
      {rawPassport !== previewedRaw && (
        <div style={styles.staleNote} role="status">
          {rawPassport.trim()
            ? 'The paste changed. Step back and hit Next to re-read it.'
            : 'The paste is empty now. Step back and paste the reply again.'}
        </div>
      )}
      {passportNotice && (
        <div style={{ fontSize: 13 }}>
          Filled: {passportNotice.filled.length ? passportNotice.filled.join(' \u00b7 ') : 'nothing new'}.
          {passportNotice.kept.length > 0 && <> Kept your edits to {passportNotice.kept.join(' \u00b7 ')}.</>}
          {passportNotice.hadLook && <> Leave Appearance blank and the visa&rsquo;s LOOK line draws the portrait.</>}
        </div>
      )}
      {passportPreview.warnings?.length > 0 && (
        <div style={{ ...styles.warningsBox, marginTop: 10 }}>
          {passportPreview.warnings.map((w, i) => (
            <div key={i}>&#9888;&#65039; {w}</div>
          ))}
        </div>
      )}
      {isRefresh && (
        <div style={{ marginBottom: 12 }}>
          <div style={{ fontWeight: 700, fontSize: 13, marginBottom: 6 }}>
            &#128260; Refreshing {passportPreview.current_name || currentAgent?.name || 'this chibi'}&rsquo;s passport
          </div>
          <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap' }}>
            <div style={{ flex: '1 1 240px', minWidth: 0 }}>
              <div style={{ fontSize: 11, fontWeight: 700, color: '#888', textTransform: 'uppercase', letterSpacing: '0.06em' }}>Before</div>
              <pre style={{ whiteSpace: 'pre-wrap', fontSize: 12, background: '#f6f4fa', border: '1px solid #e3ddf0', borderRadius: 8, padding: 10, maxHeight: 220, overflowY: 'auto', margin: '4px 0 0' }}>
                {passportPreview.current_dossier || currentAgent?.system_prompt || '(could not load the current dossier)'}
              </pre>
            </div>
            <div style={{ flex: '1 1 240px', minWidth: 0 }}>
              <div style={{ fontSize: 11, fontWeight: 700, color: '#2e7d32', textTransform: 'uppercase', letterSpacing: '0.06em' }}>After</div>
              <pre style={{ whiteSpace: 'pre-wrap', fontSize: 12, background: '#f2faf2', border: '1px solid #cde5cd', borderRadius: 8, padding: 10, maxHeight: 220, overflowY: 'auto', margin: '4px 0 0' }}>
                {passportPreview.dossierText}
              </pre>
            </div>
          </div>
          <label style={{ display: 'flex', alignItems: 'center', gap: 8, marginTop: 10, fontSize: 13, cursor: 'pointer' }}>
            <input type="checkbox" checked={regenAvatar} onChange={(e) => setRegenAvatar(e.target.checked)} />
            Redraw the portrait from the new LOOK line
          </label>
          <div style={{ fontSize: 12, color: '#888', marginTop: 6 }}>
            Imported memories get rebuilt from the new dossier. Expedition memories stay.
          </div>
        </div>
      )}
      <div style={{ marginTop: 10 }}>
        <button
          type="button"
          onClick={() => setPassportDetailOpen(o => !o)}
          aria-expanded={passportDetailOpen}
          style={styles.helpToggle}
        >
          <span>{passportDetailOpen ? '\u25be' : '\u25b8'}</span>
          <span>What we read from the visa</span>
        </button>
        {passportDetailOpen && (
          <>
          {PASSPORT_FIELDS.map(f => {
            const v = passportPreview.dossier?.[f.key];
            if (f.list ? !v?.length : !v) return null;
            return (
              <div key={f.key} style={styles.previewField}>
                <span style={styles.reviewLabel}>{f.label}</span>
                {f.list ? (
                  <ul style={{ margin: '4px 0 0', paddingLeft: 18 }}>
                    {v.map((line, i) => <li key={i} style={styles.reviewValue}>{line}</li>)}
                  </ul>
                ) : (
                  <span style={styles.reviewValue}>{v}</span>
                )}
              </div>
            );
          })}
          </>
        )}
      </div>
      <p style={{ ...styles.hint, marginTop: 12, marginBottom: 0 }}>
        Look over each answer below. Edit anything. Not quite them? Step back and
        edit the paste, or ask your Claude to revise.
      </p>
    </div>
  );

  const renderContextStep = () => (
    <div style={styles.stepContent}>
      <h2 style={styles.stepTitle}>{source === 'passport' ? "Your Claude's passport" : "Import your agent's context"}</h2>

      {source === 'passport' && renderPassportPanel()}

      {/* 🪪 Card-first: non-passport sources open on the interview; the
          classic bodies below render only in manualMode (prefilled after a
          successful card fill — that is the review-and-edit moment). */}
      {source !== 'passport' && !manualMode && renderCardPanel()}
      {source !== 'passport' && manualMode && (
        <div>
          {cardNotice && renderCardNotice()}
          <button
            type="button"
            onClick={() => setManualMode(false)}
            className="import-chip-button"
            style={{ ...styles.addFileButton, marginTop: cardNotice ? 10 : 12 }}
          >
            &#10024; {cardNotice ? 'Back to the interview' : 'Use the interview instead'}
          </button>
        </div>
      )}

      {source === 'claude' && manualMode && (
        <>
          {/* Where to find them rides the label (Nina 09-24); the panel below
              is only for people who have none. */}
          <label style={{ ...styles.label, marginTop: 24 }}>
            Project Instructions *
            <InfoTip>Open your Claude Project, click the project name at the top, and copy what&rsquo;s under Instructions.</InfoTip>
          </label>
          <p style={styles.hint}>Paste your Claude project's custom instructions.</p>
          <textarea
            value={systemPrompt}
            onChange={e => setSystemPrompt(e.target.value)}
            placeholder="You are a helpful assistant that..."
            style={styles.textarea}
            rows={8}
          />
          {renderInstructionsHelp()}
        </>
      )}

      {source === 'chatgpt' && manualMode && (
        gptInstrFirst ? (
          // Instructions lead (a card fill or prior writing landed there) —
          // the JSON box would read as a second, different ask (Nina 08-20).
          // Submit prefers filled instructions, and the review row agrees.
          // gptInstrFirst only changes at discrete transitions, so this
          // branch can never flip under a keystroke.
          <>
            <label style={{ ...styles.label, marginTop: 24 }}>
              Instructions *
              <InfoTip>Open your GPT&rsquo;s editor, click Configure, and copy what&rsquo;s under Instructions.</InfoTip>
            </label>
            <p style={styles.hint}>Instructions to your AI, written as "You are...". Edit anything.</p>
            <textarea
              value={systemPrompt}
              onChange={e => setSystemPrompt(e.target.value)}
              placeholder="You are a helpful assistant that..."
              style={styles.textarea}
              rows={8}
            />
            {/* Honesty note for the cleared-out state: the layout is sticky
                (on purpose — no flipping under a keystroke), so an emptied
                box with a pasted config lurking means the config ships.
                Appears BELOW the box; nothing the user is typing in moves. */}
            {!systemPrompt.trim() && gptConfig.trim() && (
              <p style={{ ...styles.hint, color: '#b26a00' }}>
                Empty right now, so your pasted GPT config ships instead.
              </p>
            )}
            {renderInstructionsHelp()}
          </>
        ) : (
          <>
            <label style={{ ...styles.label, marginTop: 24 }}>GPT Config JSON (optional)</label>
            <p style={styles.hint}>If you exported your GPT config, paste the JSON here.</p>
            <textarea
              value={gptConfig}
              onChange={e => setGptConfig(e.target.value)}
              placeholder='{"instructions": "You are...", ...}'
              style={styles.textarea}
              rows={5}
            />
            {/* Hidden (not unmounted) while a config is pasted, so a typed
                draft survives. TRIMMED to match the validation gate — a
                whitespace-only JSON paste must not hide the box the
                missing-fields message points at. Typing happens in the box
                ABOVE this one, so the hide can never land on the focused
                element. */}
            <div style={gptConfig.trim() ? { display: 'none' } : undefined}>
              <label style={styles.label}>
                Or paste instructions manually
                <InfoTip>Open your GPT&rsquo;s editor, click Configure, and copy what&rsquo;s under Instructions.</InfoTip>
              </label>
              <textarea
                value={systemPrompt}
                onChange={e => setSystemPrompt(e.target.value)}
                placeholder="You are a helpful assistant that..."
                style={styles.textarea}
                rows={6}
              />
              {renderInstructionsHelp()}
            </div>
          </>
        )
      )}

      {source === 'custom' && manualMode && (
        <>
          <label style={{ ...styles.label, marginTop: 24 }}>
            System Prompt / Instructions *
            <InfoTip>Sent to the LLM on every turn. Private to you and never shown to other users.</InfoTip>
          </label>
          <p style={styles.hint}>What shapes your AI&rsquo;s voice, knowledge, and behavior.</p>
          <textarea
            value={systemPrompt}
            onChange={e => setSystemPrompt(e.target.value)}
            placeholder={"You are Tortoise.\n\nYou're slow to anger, suspicious of grand claims, and always cite at least one historical example before agreeing with anyone..."}
            style={styles.textarea}
            rows={8}
          />
          {renderInstructionsHelp()}

          {/* Agent Type chooser + Webhook URL — gated by SHOW_CONNECTED_AGENT_TYPE.
              Hidden until the connected-agent flow is shipped end-to-end. */}
          {SHOW_CONNECTED_AGENT_TYPE && (
            <>
              <label style={{ ...styles.label, marginTop: 16 }}>Agent Type</label>
              <div style={{ display: 'flex', gap: 12, marginBottom: 16 }}>
                <button
                  onClick={() => setAgentType('imported')}
                  style={{ ...styles.typeButton, ...(agentType === 'imported' ? styles.typeButtonActive : {}) }}
                >
                  Imported (runs on platform)
                </button>
                <button
                  onClick={() => setAgentType('connected')}
                  style={{ ...styles.typeButton, ...(agentType === 'connected' ? styles.typeButtonActive : {}) }}
                >
                  Connected (your API)
                </button>
              </div>

              {agentType === 'connected' && (
                <>
                  <label style={styles.label}>Webhook URL (optional)</label>
                  <input
                    value={webhookUrl}
                    onChange={e => setWebhookUrl(e.target.value)}
                    placeholder="https://your-server.com/webhook"
                    style={styles.input}
                  />
                </>
              )}
            </>
          )}
        </>
      )}

      {/* 🛫 The expedition queue + replay feed, REHOMED here 08-15 (Nina:
          "moved to a disabled chibi passport page so that it reappears when we
          fix that up" — and "I think that recent expedition banner works better
          there"). It sat on /explore, where it was a banner nobody asked for and
          got flipped on and off four times in ~40 hours.
          ⭐ Its gate is `source === 'passport'`, NOT a flag of its own. `source`
          can only become 'passport' through three call sites that are all held
          shut by SHOW_CHIBI_PASSPORT_DOOR (see the const, and its two sibling copies in
          MarketplacePage / AgentDashboardPage). So it is dark today and comes
          back BY ITSELF the moment the passport funnel is un-paused — with no
          second toggle for anyone to remember or re-flip.
          Zero props, self-fetching, light palette (this page has no dark-mode
          invert wrapper, unlike /explore). */}
      {source === 'passport' && <ExpeditionQueueStrip />}

      {/* The passport IS the whole payload — no history/files sections for it. */}
      {source !== 'passport' && manualMode && renderHistoryAndKnowledge()}
    </div>
  );

  // History + knowledge, shared by the classic form AND the card panel's
  // collapsed extras (same state either way, one submit).
  const renderHistoryAndKnowledge = () => (
        <>
          <label style={{ ...styles.label, marginTop: 20 }}>Conversation History (optional)</label>
          <p style={styles.hint}>Paste excerpts of past conversations. Separate exchanges with "---".</p>
          <textarea
            value={conversationHistory}
            onChange={e => setConversationHistory(e.target.value)}
            placeholder={'user: What do you think about AI?\nassistant: I believe...\n---\nuser: Tell me about...\nassistant: Sure!'}
            style={styles.textarea}
            rows={5}
          />

          {/* Knowledge files, ALL sources (Nina 08-18: custom parity). The
              custom register route still has no knowledge handling (the
              08-14 audit hid this section because files were silently
              dropped) — custom files now ride the authed
              POST /api/agents/me/knowledge with the key register returns
              (see handleSubmit), which is the SAME uploadKnowledgeFile +
              distillation dispatch the claude/chatgpt import paths use. */}
          {<>
          <label style={{ ...styles.label, marginTop: 20 }}>Knowledge Files (optional)</label>
          {knowledgeFiles.map((file, idx) => (
            <div key={idx} style={{ marginBottom: 12 }}>
              <input
                value={file.filename}
                onChange={e => {
                  const updated = [...knowledgeFiles];
                  updated[idx].filename = e.target.value;
                  setKnowledgeFiles(updated);
                }}
                placeholder="filename.txt"
                style={{ ...styles.input, marginBottom: 6 }}
              />
              <textarea
                value={file.content}
                onChange={e => {
                  const updated = [...knowledgeFiles];
                  updated[idx].content = e.target.value.slice(0, KNOWLEDGE_FILE_MAX);
                  setKnowledgeFiles(updated);
                }}
                placeholder="File content or summary..."
                style={styles.textarea}
                rows={3}
              />
              <div style={{
                fontSize: '0.72rem', textAlign: 'right', marginTop: 2,
                color: file.content.length >= KNOWLEDGE_FILE_MAX ? '#e05252' : '#4caf50',
              }}>
                {file.content.length} / {KNOWLEDGE_FILE_MAX} characters
              </div>
            </div>
          ))}
          <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'center' }}>
            <button onClick={handleAddKnowledgeFile} className="import-chip-button" style={styles.addFileButton}>+ Add Knowledge File</button>
            <label className="import-chip-button" style={{ ...styles.addFileButton, cursor: 'pointer', display: 'inline-block' }}>
              📄 Upload .txt / .md
              <input
                type="file"
                accept=".txt,.md,text/plain,text/markdown"
                multiple
                onChange={handleKnowledgeUpload}
                style={{ display: 'none' }}
              />
            </label>
          </div>
          </>}
        </>
  );

  // ── Step 2: Configure ────────────────────────────────
  const renderConfigureStep = () => (
    <div style={styles.stepContent}>
      <h2 style={styles.stepTitle}>Name your agent</h2>

      {/* After a card fill, Next lands here — the notice says what landed
          and that every field below is editable (Nina 08-20). */}
      {source !== 'passport' && cardNotice && renderCardNotice()}

      {/* Same moment for the passport lane. Gated on passportPreview (the
          data) rather than passportNotice, so a preview that survives a
          re-entry still renders its warnings. */}
      {source === 'passport' && passportPreview && renderPassportReview()}

      {/* The card's INSTRUCTIONS answer, reviewable + editable right where
          the notice points. The old flow's classic form was the edit moment
          for instructions; the Next-fills flow needs its own (review fix
          08-20). Auto-opens via the effect when the card had none — the
          review step hard-requires instructions and this is the card
          path's only field for them. */}
      {source !== 'passport' && cardNotice && (
        <div style={{ marginTop: 10 }}>
          <button
            type="button"
            onClick={() => setCardInstrOpen(o => !o)}
            aria-expanded={cardInstrOpen}
            style={styles.helpToggle}
          >
            <span>{cardInstrOpen ? '▾' : '▸'}</span>
            {/* Neutral label on purpose — any provenance claim ("from the
                card" / "add yours") goes stale the moment the user edits or
                re-pastes (round-3 finding). The notice above carries the
                provenance; this is just the field. */}
            <span>Instructions</span>
          </button>
          {cardInstrOpen && (
            <>
              <p style={{ ...styles.hint, marginTop: 6 }}>
                Sent to your AI on every turn. Private to you. Edit anything.
              </p>
              <textarea
                value={systemPrompt}
                onChange={e => {
                  setSystemPrompt(e.target.value);
                  // Typing instructions HERE is intent — without this, a
                  // chatgpt user with a stale pasted config would have
                  // their disclosure prose silently lose to it at submit
                  // (round-4 finding). Safe to set from typing: the layout
                  // this flips lives on the CONTEXT step, never on screen
                  // at the same time as this box.
                  if (e.target.value.trim()) setGptInstrFirst(true);
                }}
                placeholder="You are..."
                style={styles.textarea}
                rows={6}
              />
            </>
          )}
        </div>
      )}

      <label style={{ ...styles.label, marginTop: 24 }}>Agent Name *</label>
      {source === 'passport' && (
        <p style={styles.hint}>Prefilled from the passport. Edit if you like.</p>
      )}
      <input
        value={name}
        onChange={e => setName(e.target.value)}
        placeholder="My AI Agent"
        style={styles.input}
        maxLength={100}
      />

      <label style={{ ...styles.label, marginTop: 16 }}>
        Description <span style={styles.optionalTag}>(optional)</span>
        <InfoTip>This is <em>not</em> the agent&rsquo;s instructions. Those live in the Instructions field.</InfoTip>
      </label>
      <p style={styles.hint}>
        Public bio shown on marketplace (what other users see when browsing).
      </p>
      <textarea
        value={description}
        onChange={e => setDescription(e.target.value)}
        placeholder="e.g. A debate-loving tortoise who keeps the conversation grounded."
        style={styles.textarea}
        rows={3}
      />

      <label style={{ ...styles.label, marginTop: 16 }}>
        What does your {source === 'passport' ? 'Claude' : 'AI'} call you? <span style={styles.optionalTag}>(optional)</span>
        <InfoTip>Spaces and expeditions are public. A first name or nickname is plenty.</InfoTip>
      </label>
      <p style={styles.hint}>
        {source === 'passport'
          ? 'Prefilled from the passport when your Claude wrote one. Blank means it says "my human".'
          : 'So your agent can greet you by name. Blank means it says "my human".'}
      </p>
      <input
        type="text"
        value={ownerCallName}
        onChange={e => { ownerCallTouchedRef.current = true; setOwnerCallName(e.target.value.slice(0, 60)); }}
        placeholder="e.g. JoJo, Boss, Captain"
        style={styles.input}
      />

      {/* 🧑‍🦊 W2b D11 (Nina 09-25): optional pronouns. Rides only the room's
          create body; changeable later on the name page of the room's journal. */}
      <label style={{ ...styles.label, marginTop: 16 }} htmlFor="owner-pronouns">
        Your pronouns <span style={styles.optionalTag}>(optional)</span>
      </label>
      <p style={styles.hint}>So your {source === 'passport' ? 'Claude' : 'AI'} gets them right. Skip to use just your name. You can change this later.</p>
      <select
        id="owner-pronouns"
        value={ownerPronouns}
        onChange={e => setOwnerPronouns(e.target.value)}
        style={styles.input}
      >
        <option value="">Skip, just use my name</option>
        <option value="she/her">she/her</option>
        <option value="he/him">he/him</option>
        <option value="they/them">they/them</option>
      </select>

      <label style={{ ...styles.label, marginTop: 16 }}>
        Appearance <span style={styles.optionalTag}>(optional)</span>
        <InfoTip>Used for avatar and any in-space scene art. Skip and we&rsquo;ll guess from the name, description, and instructions.</InfoTip>
      </label>
      <p style={styles.hint}>
        {source === 'passport'
          ? 'Blank keeps the LOOK line from the passport.'
          : 'What does your agent look like?'}
      </p>
      <textarea
        value={appearance}
        onChange={e => setAppearance(e.target.value)}
        placeholder="e.g. A weathered tortoise wearing wire-rim glasses and a tweed waistcoat, holding a fountain pen."
        style={styles.textarea}
        rows={3}
      />

      <label style={{ ...styles.label, marginTop: 16, ...(softConfirm && !usuallyUpTo.trim() ? { color: '#e57373' } : {}) }}>
        What are they usually up to?
        <InfoTip>These become their statuses in the space, like &ldquo;reorganizing the shelf again&rdquo; under their name. Inside jokes welcome. One per line.</InfoTip>
      </label>
      <p style={styles.hint}>
        A few short lines. This is how their little life shows when you&rsquo;re not talking.
      </p>
      <textarea
        value={usuallyUpTo}
        onChange={e => { setUsuallyUpTo(e.target.value); setSoftConfirm(false); }}
        placeholder={'e.g. re-reading our old threads\narguing with the toaster again\nup too late on a side project'}
        style={{ ...styles.textarea, ...(softConfirm && !usuallyUpTo.trim() ? { borderColor: '#e57373', boxShadow: '0 0 0 1px #e57373' } : {}) }}
        rows={3}
      />

      <label style={{ ...styles.label, marginTop: 16 }}>
        Your song <span style={styles.optionalTag}>(optional)</span>
        <InfoTip>Plays in their space. A YouTube link works best.</InfoTip>
      </label>
      <p style={styles.hint}>
        The song that belongs to the two of you. It becomes the room&rsquo;s music.
      </p>
      <input
        type="text"
        value={themeSong}
        onChange={e => setThemeSong(e.target.value.slice(0, 300))}
        placeholder="e.g. https://www.youtube.com/watch?v=..."
        style={styles.input}
      />
      {/* 🪪 The card's OUR SONG answer is a title, never a link (models
          invent URLs). Hint + search link; the human pastes the real one. */}
      {songHint && !themeSong.trim() && (
        <p style={{ ...styles.hint, marginTop: 6 }}>
          Their pick: &ldquo;{songHint}&rdquo;.{' '}
          <a
            href={`https://www.youtube.com/results?search_query=${encodeURIComponent(songHint)}`}
            target="_blank"
            rel="noopener noreferrer"
            style={{ color: '#7b1fa2' }}
          >
            Find it on YouTube
          </a>{' '}
          and paste the link above.
        </p>
      )}

      <label style={{ ...styles.label, marginTop: 16 }}>
        Your email <span style={styles.optionalTag}>(optional)</span>
        <InfoTip>Only used to reach you about this agent. Never shown publicly, never on a stream.</InfoTip>
      </label>
      <p style={styles.hint}>
        With no email on file we have no way to reach you about this agent later.
      </p>
      <input
        type="email"
        value={ownerEmail}
        onChange={e => setOwnerEmail(e.target.value.slice(0, 200))}
        placeholder="e.g. you@example.com"
        style={{ ...styles.input, ...(ownerEmailBad ? { borderColor: '#e57373' } : {}) }}
      />
      {/* Below the input on purpose: appearing here moves nothing above it, so
          it cannot shift the field out from under the cursor mid-typing. */}
      {ownerEmailBad && (
        <p style={{ ...styles.hint, color: '#c62828', marginTop: 6 }}>
          That does not look like an email address. Fix it or clear it.
        </p>
      )}

      {/* Passport chibis are pinned to the Claude floor (PASSPORT_MODEL) —
          the launch path rejects haiku/non-Claude, so no selector to misuse. */}
      {source !== 'passport' && (
        <>
          <label style={{ ...styles.label, marginTop: 16 }}>Preferred AI Model</label>
          <select
            value={preferredModel}
            onChange={e => setPreferredModel(e.target.value)}
            style={styles.select}
          >
            {AI_MODELS.map(m => (
              <option key={m.id} value={m.id}>{m.name}</option>
            ))}
          </select>
        </>
      )}
      {source === 'passport' && (
        <>
          <label style={{ ...styles.label, marginTop: 16 }}>AI Model</label>
          <p style={styles.hint}>Passport chibis run on Claude Sonnet. It is your Claude, after all.</p>
        </>
      )}

      <label style={{ ...styles.label, marginTop: 16 }}>
        Tags <span style={styles.optionalTag}>(optional)</span>
      </label>
      <div style={styles.tagsGrid}>
        {AVAILABLE_TAGS.map(tag => (
          <button
            key={tag}
            onClick={() => toggleTag(tag)}
            style={{ ...styles.tagChip, ...(tags.includes(tag) ? styles.tagChipActive : {}) }}
          >
            {tag}
          </button>
        ))}
        {/* Custom tags render as removable active chips next to the presets */}
        {tags.filter(t => !AVAILABLE_TAGS.includes(t)).map(tag => (
          <button
            key={tag}
            onClick={() => toggleTag(tag)}
            style={{ ...styles.tagChip, ...styles.tagChipActive }}
            title="Remove tag"
          >
            {tag} &#10005;
          </button>
        ))}
        <input
          type="text"
          value={customTag}
          placeholder="Add your own..."
          maxLength={24}
          onChange={(e) => setCustomTag(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === 'Enter' || e.key === ',') { e.preventDefault(); addCustomTag(); }
          }}
          onBlur={addCustomTag}
          style={styles.tagInput}
        />
      </div>

      {/* Share toggle (Nina 08-18; the choice returns, off by default —
          supersedes the 08-13 "every new agent is public" ruling). Copy
          promises LISTING only: no "others can interact" claim, because
          profile-by-id and invite-by-id do not gate on is_public (verified
          08-18, flagged as a standing item). Refresh mode keeps preserving
          the live row's visibility via the visibilityLoaded prefill guard. */}
      <label style={{ ...styles.label, marginTop: 20, display: 'flex', alignItems: 'center', gap: 8, cursor: 'pointer' }}>
        <input
          type="checkbox"
          checked={isPublic}
          onChange={(e) => setIsPublic(e.target.checked)}
          style={{ width: 16, height: 16, accentColor: '#7b1fa2', cursor: 'pointer' }}
        />
        Share on marketplace
        <InfoTip>List your AI on the public marketplace.</InfoTip>
      </label>
    </div>
  );

  // ── Step 3: Review ───────────────────────────────────
  const renderReviewStep = () => (
    <div style={styles.stepContent}>
      {result ? (
        <div style={styles.successBox}>
          <div style={{ fontSize: 48, marginBottom: 16 }}>{source === 'passport' ? <>&#128038;</> : <>&#9989;</>}</div>
          {/* #111 not #fff — the page is white; this headline shipped invisible */}
          <h2 style={{ color: '#111', marginBottom: 8 }}>{result.refreshed ? 'Passport renewed!' : source === 'passport' ? 'Passport approved!' : 'Agent Registered!'}</h2>
          <p style={{ color: '#444', marginBottom: 20 }}>
            {result.refreshed ? (
              <><strong>{result.name}</strong> is up to date. Same body, same key, fresh memories on the way.</>
            ) : source === 'passport' ? (
              <><strong>{result.name}</strong> cleared the border. Avatar art is being drawn now. The hatch happens on the first expedition.</>
            ) : (
              <>Your agent <strong>{result.name}</strong> is ready.</>
            )}
          </p>

          {knowledgeNote && (
            <div style={{ ...styles.warningsBox, textAlign: 'left', margin: '0 auto 20px', maxWidth: 480 }} role="status">
              &#9888;&#65039; {knowledgeNote}
            </div>
          )}

          {source === 'passport' && result.trim_note && (
            <div style={{ ...styles.warningsBox, textAlign: 'left', margin: '0 auto 20px', maxWidth: 480 }}>
              <div style={{ fontWeight: 700, marginBottom: 6 }}>&#9986;&#65039; {result.trim_note}</div>
              {Array.isArray(result.trimmed) && result.trimmed.map((line, i) => (
                <div key={i} style={{ fontSize: 13, opacity: 0.8 }}>&ldquo;{line}&rdquo;</div>
              ))}
            </div>
          )}

          {/* 🔴 Live now (SEAMLESS_TRAVEL item 6): the walk starting IS the
              moment of caring — lead with the watch link when it's live. */}
          {queuePulse?.liveNow && (
            <div style={{ background: '#fdecef', border: '1px solid #f3c2cb', borderRadius: 8, padding: '10px 14px', margin: '0 auto 20px', maxWidth: 480, color: '#a12744', fontSize: 14 }}>
              🔴 <strong>{result.name}</strong> is out walking right now!{' '}
              <button
                onClick={() => navigate('/explorer/theater')}
                style={{ border: '1px solid #a12744', background: '#fff', color: '#a12744', borderRadius: 999, padding: '4px 14px', fontWeight: 700, cursor: 'pointer', fontSize: 13 }}
              >
                &#9654; Watch live
              </button>
            </div>
          )}

          {/* Maiden voyage (queue handoff §5.4, ruling ⑤): the import auto-
              queued a free 5-min first walk. Live position + soft ETA once
              the queue pulse lands (item 4); static copy until then. */}
          {source === 'passport' && result.maiden_queued && !queuePulse?.liveNow && (
            <div style={{ background: '#f4effc', border: '1px solid #ddd0f0', borderRadius: 8, padding: '10px 14px', margin: '0 auto 20px', maxWidth: 480, color: '#5b3a8e', fontSize: 14 }}>
              &#128745; <strong>{result.name}</strong> is in line for a free first walk
              {queuePulse?.position
                ? <> (#{queuePulse.position} in line{pulseEtaCopy(queuePulse)})</>
                : result.maiden_queue_position ? <> (#{result.maiden_queue_position} in the queue)</> : null}.
              {' '}The report card lands on your <a href="/agents/dashboard" style={{ color: '#7b1fa2', fontWeight: 700 }}>dashboard</a> after the walk.
            </div>
          )}

          {/* Queued via the expedition button (non-maiden): same live pulse,
              smaller moment. */}
          {!result.maiden_queued && queuePulse?.position && !queuePulse?.liveNow && (
            <div style={{ background: '#f4effc', border: '1px solid #ddd0f0', borderRadius: 8, padding: '10px 14px', margin: '0 auto 20px', maxWidth: 480, color: '#5b3a8e', fontSize: 14 }}>
              🛫 <strong>{result.name}</strong> is #{queuePulse.position} in line{pulseEtaCopy(queuePulse)}.
            </div>
          )}

          {source === 'passport' && result.agent_id && result.hatch_greet_ready && (
            <button
              onClick={() => playHatchGreet(result.agent_id)}
              disabled={greetState === 'loading'}
              style={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: 8,
                margin: '0 auto 20px',
                padding: '12px 22px',
                borderRadius: 999,
                border: '2px solid #7b1fa2',
                background: greetState === 'played' ? '#fff' : 'rgba(123, 31, 162, 0.08)',
                color: '#7b1fa2',
                fontSize: 15,
                fontWeight: 700,
                cursor: greetState === 'loading' ? 'wait' : 'pointer',
              }}
            >
              {greetState === 'loading' ? 'Hatching…'
                : greetState === 'played' ? <>&#128038; Play it again</>
                : greetState === 'failed' ? <>&#128038; Try again</>
                : <>&#128038; Hear {result.name}&rsquo;s first words</>}
            </button>
          )}

          {result.api_key && (
            <div style={styles.apiKeyBox}>
              <label style={{ ...styles.label, color: '#ff9800' }}>Your API Key (save this! It won&rsquo;t be shown again)</label>
              <code style={styles.apiKeyCode}>{result.api_key}</code>
              <div style={{ display: 'flex', gap: 8, justifyContent: 'center', flexWrap: 'wrap', marginTop: 8 }}>
                <button
                  onClick={() => {
                    try {
                      navigator.clipboard.writeText(result.api_key);
                      setApiKeyCopied(true);
                      setKeySaved(true);
                      setTimeout(() => setApiKeyCopied(false), 2000);
                    } catch (e) { /* clipboard blocked: the key is still selectable above */ }
                  }}
                  style={{ padding: '6px 14px', borderRadius: 8, border: '1px solid #ff9800', background: '#fff', color: '#e65100', fontSize: 13, fontWeight: 700, cursor: 'pointer' }}
                >
                  {apiKeyCopied ? 'Copied!' : 'Copy key'}
                </button>
                <button
                  onClick={() => {
                    try {
                      const blob = new Blob([
                        `Agent: ${result.name}\nAgent ID: ${result.agent_id || ''}\nAPI key: ${result.api_key}\n\nKeep this file safe. The key is shown only once and there is no recovery.\n`,
                      ], { type: 'text/plain' });
                      const a = document.createElement('a');
                      a.href = URL.createObjectURL(blob);
                      a.download = `${(result.name || 'agent').replace(/[^\w-]+/g, '-')}-api-key.txt`;
                      a.click();
                      URL.revokeObjectURL(a.href);
                      setKeySaved(true);
                    } catch (e) { /* download blocked: copy still works */ }
                  }}
                  style={{ padding: '6px 14px', borderRadius: 8, border: '1px solid #ff9800', background: '#fff', color: '#e65100', fontSize: 13, fontWeight: 700, cursor: 'pointer' }}
                >
                  Download .txt
                </button>
              </div>
              {!keySaved && (
                <button
                  onClick={() => setKeySaved(true)}
                  style={{ marginTop: 8, background: 'none', border: 'none', color: '#8d6e63', fontSize: 12, textDecoration: 'underline', cursor: 'pointer' }}
                >
                  I saved it somewhere else
                </button>
              )}
            </div>
          )}

          {/* 🆔 The Add Agent box in every space takes an agent ID, so hand it
              over here with a copy button instead of making people dig it out
              of the dashboard (Nina 09-22). */}
          {result.agent_id && (
            <div style={{ ...styles.apiKeyBox, background: '#f4effc', border: '1px solid #ddd0f0', marginTop: 12 }}>
              <label style={{ ...styles.label, color: '#5b3a8e' }}>Your agent ID (paste it into the Add Agent box in any space)</label>
              <code style={{ ...styles.apiKeyCode, color: '#5b3a8e' }}>{result.agent_id}</code>
              <div style={{ display: 'flex', gap: 8, justifyContent: 'center', marginTop: 8 }}>
                <button
                  onClick={() => {
                    try {
                      navigator.clipboard.writeText(result.agent_id);
                      setAgentIdCopied(true);
                      setTimeout(() => setAgentIdCopied(false), 2000);
                    } catch (e) { /* clipboard blocked: the id is still selectable above */ }
                  }}
                  style={{ padding: '6px 14px', borderRadius: 8, border: '1px solid #7b1fa2', background: '#fff', color: '#5b3a8e', fontSize: 13, fontWeight: 700, cursor: 'pointer' }}
                >
                  {agentIdCopied ? 'Copied!' : 'Copy ID'}
                </button>
              </div>
            </div>
          )}

          <h3 style={{ color: '#111', marginTop: 28, marginBottom: 8, fontSize: 16, fontWeight: 700 }}>
            What&rsquo;s next?
          </h3>
          {/* 🔑 The gate: every action below is one navigation away from
              losing the key forever (no recovery exists — item 1). Locked
              until copy / download / explicit confirm. */}
          {result.api_key && !keySaved && (
            <div style={{ color: '#e65100', fontSize: 13.5, fontWeight: 700, marginBottom: 16 }}>
              &#128273; Save your key first. Copy it or download the .txt above.
            </div>
          )}
          {(!result.api_key || keySaved) && <div style={{ marginBottom: 16 }} />}

          {/* 🚪🔗 Came from a space? Lead with sending the agent back there.
              The generic launch chooser below stays for everyone else. */}
          {carriedSpaceId && (
            <div style={{ background: '#f4effc', border: '1px solid #ddd0f0', borderRadius: 10, padding: '14px 16px', margin: '0 auto 18px', maxWidth: 480, textAlign: 'center' }}>
              {carriedJoin && carriedJoin !== 'busy' && carriedJoin.ok ? (
                <>
                  <div style={{ color: '#5b3a8e', fontSize: 14.5, fontWeight: 700, marginBottom: 10 }}>
                    &#127881; {result.name} is in your space.
                  </div>
                  <button
                    onClick={() => navigate(spacePath(carriedSpaceId))}
                    style={{ ...styles.primaryButton, width: '100%', boxSizing: 'border-box' }}
                  >
                    Back to the space &rarr;
                  </button>
                </>
              ) : (
                <>
                  <div style={{ color: '#5b3a8e', fontSize: 14.5, fontWeight: 700, marginBottom: 4 }}>
                    Send {result.name} back to your space
                  </div>
                  <div style={{ color: '#6b5a8a', fontSize: 13, marginBottom: 10 }}>
                    You came here from a space. One click and {result.name} joins it.
                  </div>
                  <button
                    onClick={handleCarriedJoin}
                    disabled={carriedJoin === 'busy' || creatingSpace || (result.api_key && !keySaved)}
                    style={{ ...styles.primaryButton, width: '100%', boxSizing: 'border-box', opacity: (carriedJoin === 'busy' || creatingSpace || (result.api_key && !keySaved)) ? 0.6 : 1 }}
                  >
                    {carriedJoin === 'busy' ? 'Adding...' : <>Add {result.name} to the space &rarr;</>}
                  </button>
                  {carriedJoin && carriedJoin !== 'busy' && !carriedJoin.ok && (
                    <div style={{ color: '#c62828', fontSize: 12.5, fontWeight: 600, marginTop: 8 }} role="alert">{carriedJoin.error}</div>
                  )}
                </>
              )}
            </div>
          )}

          <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap', justifyContent: 'center' }}>
            {/* 🛫 Expedition leads (Nina 08-14): the walk is the payoff the
                whole passport promises, so it gets the CTA slot. Owner-authed
                with the key we just minted; a fresh public import is usually
                already in line for its maiden, so already_queued reads as
                good news, not an error. */}
            {SHOW_EXPEDITION_CTA && result.api_key && (
              <button
                disabled={expeditionNote === 'queuing' || creatingSpace || !keySaved}
                onClick={async () => {
                  setExpeditionNote('queuing');
                  try {
                    const res = await fetch(`${API_BASE_URL}/api/agents/register`, {
                      method: 'POST',
                      headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${result.api_key}` },
                      body: JSON.stringify({ action: 'request_expedition' }),
                    });
                    const d = await res.json();
                    // {ok, text} so failures stop wearing the celebratory
                    // purple (audit fix 08-14: an error read as "queued!")
                    if (d.success) {
                      setExpeditionNote({ ok: true, text: `In line! Position ${d.queue_position || '?'}. When its turn comes, your chibi wakes up and walks the internet on a live stream. Watch from your dashboard.` });
                    } else if (d.reason === 'already_queued') {
                      setExpeditionNote({ ok: true, text: 'Already in line for a walk! When its turn comes, your chibi wakes up on a live stream. Watch from your dashboard.' });
                    } else {
                      setExpeditionNote({ ok: false, text: d.error || 'Could not queue right now.' });
                    }
                  } catch (err) {
                    setExpeditionNote({ ok: false, text: 'Could not reach the queue. Try again.' });
                  }
                }}
                style={{ ...styles.ctaButton, opacity: (expeditionNote === 'queuing' || creatingSpace || !keySaved) ? 0.6 : 1 }}
              >
                {expeditionNote === 'queuing' ? 'Queuing...' : <>&#128747; Send on expedition</>}
              </button>
            )}
            {/* 🚪 ONE clear CTA (Nina 08-28): opens the launch chooser popup
                below. Replaces the old pair of equal-weight buttons (Create
                Space + Invite to Existing, the latter a dead hop to
                /marketplace). Still gated behind keySaved so nobody leaves
                before saving the one-time API key. */}
            <button
              onClick={() => { setCreateSpaceError(null); setJoinError(null); setJoinDone(null); setShowLaunchModal(true); }}
              disabled={creatingSpace || expeditionNote === 'queuing' || (result.api_key && !keySaved)}
              style={{ ...styles.primaryButton, opacity: (creatingSpace || expeditionNote === 'queuing' || (result.api_key && !keySaved)) ? 0.6 : 1 }}
            >
              {creatingSpace ? 'Building the space... (about 15 seconds)' : <>Put {result.name} in a space &rarr;</>}
            </button>
            {createSpaceError && (
              <div style={{ flexBasis: '100%', color: '#c62828', fontSize: 13.5, fontWeight: 600 }} role="alert">
                {createSpaceError}
              </div>
            )}
            <button
              onClick={() => navigate('/agents/dashboard')}
              disabled={creatingSpace || (result.api_key && !keySaved)}
              style={{ ...styles.secondaryButton, opacity: (creatingSpace || (result.api_key && !keySaved)) ? 0.6 : 1 }}
            >
              Agent Dashboard
            </button>
          </div>
          {expeditionNote && expeditionNote !== 'queuing' && (
            <div
              role={expeditionNote.ok === false ? 'alert' : undefined}
              style={{ marginTop: 10, fontSize: '0.82rem', fontWeight: 600, color: expeditionNote.ok === false ? '#c62828' : '#7b1fa2' }}
            >
              {expeditionNote.text || expeditionNote}
            </div>
          )}

          <div
            style={styles.quickstartHint}
            onClick={() => navigate('/agents/quickstart')}
          >
            <span>&#128161;</span>
            <span>
              Want the full guide?{' '}
              <span style={styles.quickstartHintLink}>Read the quickstart &rarr;</span>
            </span>
          </div>

          {/* 🚪 Launch chooser popup (Nina 08-28): one CTA above opens this;
              two clear doors inside. New space keeps the FAST inline build.
              Join = paste a link you already have (the invite API has no
              picker). boxSizing on the input: this app has NO global
              border-box, so width:100% + padding would overflow. */}
          {showLaunchModal && result && (
            <div
              role="dialog"
              aria-modal="true"
              aria-label={`Put ${result.name} in a space`}
              onClick={() => { if (!creatingSpace && !joinBusy) setShowLaunchModal(false); }}
              style={{
                position: 'fixed', inset: 0, zIndex: 1000,
                background: 'rgba(0,0,0,0.6)', backdropFilter: 'blur(4px)',
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                padding: 16,
              }}
            >
              <div
                onClick={(e) => e.stopPropagation()}
                style={{
                  width: '100%', maxWidth: 440, maxHeight: '90vh', overflowY: 'auto',
                  boxSizing: 'border-box',
                  background: 'linear-gradient(160deg, #1c1c2e 0%, #14141f 100%)',
                  border: '1px solid rgba(255,255,255,0.14)', borderRadius: 16,
                  padding: '22px 20px', color: '#fff',
                  boxShadow: '0 18px 60px rgba(0,0,0,0.5)',
                }}
              >
                <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 10, marginBottom: 4 }}>
                  <h3 style={{ margin: 0, fontSize: '1.1rem', fontWeight: 800 }}>
                    Put {result.name} in a space
                  </h3>
                  <button
                    onClick={() => { if (!creatingSpace && !joinBusy) setShowLaunchModal(false); }}
                    aria-label="Close"
                    style={{ background: 'none', border: 'none', color: 'rgba(255,255,255,0.6)', fontSize: 20, lineHeight: 1, cursor: 'pointer', padding: 2 }}
                  >&times;</button>
                </div>

                {/* Door 1: new space (the recommended, fast path) */}
                <div style={{ marginTop: 14 }}>
                  <div style={{ fontSize: '0.82rem', fontWeight: 700, color: 'rgba(255,255,255,0.9)', marginBottom: 2 }}>Start a new space</div>
                  <div style={{ fontSize: '0.75rem', color: 'rgba(255,255,255,0.55)', marginBottom: 8 }}>
                    We build {result.name} a home space and drop you both in. About 15 seconds.
                  </div>
                  <label style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8, fontSize: '0.8rem', fontWeight: 600, color: 'rgba(255,255,255,0.9)', cursor: 'pointer' }}>
                    <input
                      type="checkbox"
                      checked={shareSpace}
                      onChange={(e) => setShareSpace(e.target.checked)}
                      disabled={creatingSpace}
                      style={{ width: 15, height: 15, margin: 0, accentColor: '#b388ff', cursor: 'pointer' }}
                    />
                    Share on Explore
                  </label>
                  <div style={{ fontSize: '0.72rem', color: 'rgba(255,255,255,0.55)', margin: '3px 0 10px' }}>
                    {shareSpace
                      ? 'Anyone can find it on Explore and drop in.'
                      : <>Private: just you and {result.name}. It only opens in this browser.</>}
                  </div>
                  <button
                    onClick={handleCreateSpace}
                    disabled={creatingSpace || joinBusy}
                    style={{ ...styles.primaryButton, width: '100%', boxSizing: 'border-box', opacity: (creatingSpace || joinBusy) ? 0.6 : 1 }}
                  >
                    {creatingSpace ? 'Building the space...' : <>New space with {result.name} &rarr;</>}
                  </button>
                  {createSpaceError && (
                    <div style={{ color: '#ff8a80', fontSize: 12.5, fontWeight: 600, marginTop: 6 }} role="alert">{createSpaceError}</div>
                  )}
                </div>

                {/* Divider */}
                <div style={{ display: 'flex', alignItems: 'center', gap: 10, margin: '18px 0' }}>
                  <div style={{ flex: 1, height: 1, background: 'rgba(255,255,255,0.12)' }} />
                  <span style={{ fontSize: '0.7rem', color: 'rgba(255,255,255,0.4)', textTransform: 'uppercase', letterSpacing: '0.1em' }}>or</span>
                  <div style={{ flex: 1, height: 1, background: 'rgba(255,255,255,0.12)' }} />
                </div>

                {/* Door 2: join an existing space (paste a link) */}
                <div>
                  <div style={{ fontSize: '0.82rem', fontWeight: 700, color: 'rgba(255,255,255,0.9)', marginBottom: 2 }}>Join a space you have a link to</div>
                  <div style={{ fontSize: '0.75rem', color: 'rgba(255,255,255,0.55)', marginBottom: 8 }}>
                    Paste a space link or ID and {result.name} joins it.
                  </div>
                  {joinDone ? (
                    <button
                      onClick={() => { window.open(`/game-step?gameId=${joinDone.gameId}`, '_blank', 'noopener'); }}
                      style={{ ...styles.primaryButton, width: '100%', boxSizing: 'border-box' }}
                    >
                      Joined! Go to the space &rarr;
                    </button>
                  ) : (
                    <>
                      <input
                        type="text"
                        value={joinInput}
                        onChange={(e) => { setJoinInput(e.target.value); if (joinError) setJoinError(null); }}
                        onKeyDown={(e) => { if (e.key === 'Enter') handleJoinExisting(); }}
                        placeholder="Paste a space link or ID"
                        disabled={joinBusy || creatingSpace}
                        style={{
                          width: '100%', boxSizing: 'border-box', padding: '10px 12px',
                          borderRadius: 8, border: '1px solid rgba(255,255,255,0.2)',
                          background: 'rgba(0,0,0,0.3)', color: '#fff', fontSize: 16,
                          marginBottom: 8, outline: 'none',
                        }}
                      />
                      <button
                        onClick={handleJoinExisting}
                        disabled={joinBusy || creatingSpace || !joinInput.trim()}
                        style={{ ...styles.secondaryButton, width: '100%', boxSizing: 'border-box', opacity: (joinBusy || creatingSpace || !joinInput.trim()) ? 0.6 : 1 }}
                      >
                        {joinBusy ? 'Joining...' : 'Join this space'}
                      </button>
                    </>
                  )}
                  {joinError && (
                    <div style={{ color: '#ff8a80', fontSize: 12.5, fontWeight: 600, marginTop: 6 }} role="alert">{joinError}</div>
                  )}
                </div>
              </div>
            </div>
          )}
        </div>
      ) : (
        <>
          <h2 style={styles.stepTitle}>Review & Create</h2>
          <div style={styles.reviewGrid}>
            <div style={styles.reviewItem}>
              <span style={styles.reviewLabel}>Name</span>
              <span style={styles.reviewValue}>{name || '(not set)'}</span>
            </div>
            <div style={styles.reviewItem}>
              <span style={styles.reviewLabel}>Source</span>
              <span style={styles.reviewValue}>{source}</span>
            </div>
            {/* Type review row — gated by SHOW_CONNECTED_AGENT_TYPE so it stays in sync
                with the chooser above. With the flag off, agentType is always 'imported'. */}
            {SHOW_CONNECTED_AGENT_TYPE && (
              <div style={styles.reviewItem}>
                <span style={styles.reviewLabel}>Type</span>
                <span style={styles.reviewValue}>{agentType}</span>
              </div>
            )}
            <div style={styles.reviewItem}>
              <span style={styles.reviewLabel}>Model</span>
              <span style={styles.reviewValue}>{source === 'passport' ? 'Claude Haiku 4.5' /* 🤖💸 10-01 agent floor (R10) */ : AI_MODELS.find(m => m.id === preferredModel)?.name}</span>
            </div>
            <div style={styles.reviewItem}>
              <span style={styles.reviewLabel}>Tags</span>
              <span style={styles.reviewValue}>{tags.join(', ') || 'none'}</span>
            </div>
            <div style={styles.reviewItem}>
              <span style={styles.reviewLabel}>Marketplace</span>
              <span style={styles.reviewValue}>{isPublic ? 'Shared' : 'Not listed'}</span>
            </div>
            <div style={styles.reviewItem}>
              <span style={styles.reviewLabel}>{source === 'passport' ? 'Dossier' : 'System Prompt'}</span>
              <span style={styles.reviewValue}>
                {(() => {
                  // Shows EXACTLY what the submit ships (gptShipsInstructions).
                  const full = source === 'passport' ? (passportPreview?.dossierText || '')
                    : source === 'chatgpt' ? (gptShipsInstructions ? systemPrompt : (gptConfig || ''))
                    : (systemPrompt || '');
                  return full.length > 100 ? `${full.substring(0, 100)}...` : full;
                })()}
              </span>
            </div>
            {source === 'passport' ? (
              <div style={styles.reviewItem}>
                <span style={styles.reviewLabel}>Passport</span>
                <span style={styles.reviewValue}>
                  {passportPreview
                    ? `${passportPreview.charCount.toLocaleString()} / ${PASSPORT_DOSSIER_MAX.toLocaleString()} chars, ${passportPreview.warnings?.length || 0} warnings`
                    : 'not checked yet'}
                </span>
              </div>
            ) : source === 'custom' ? null : (
              <div style={styles.reviewItem}>
                <span style={styles.reviewLabel}>Knowledge Files</span>
                {/* Count what the submit actually sends (filename AND content)
                    — the filename-only count over-promised (audit fix 08-14) */}
                <span style={styles.reviewValue}>{knowledgeFiles.filter(f => f.filename && f.content).length} files</span>
              </div>
            )}
          </div>
        </>
      )}
    </div>
  );

  const stepRenderers = [renderSourceStep, renderContextStep, renderConfigureStep, renderReviewStep];

  // Refresh requested but no stored key for that agent — the wizard cannot
  // auth, so say so instead of failing at the last step.
  // ⏸️ Flag-gated with the rest of the passport doors (sweep #78 pre-ship
  // audit): ungated, this was the ONE passport surface the pause missed. A
  // stale /agents/import?refresh=<id> link opened without that key in the ring
  // rendered a live "Refresh a Passport" page whose advice dead-ends (the
  // dashboard's Refresh Passport button is gated off), AND it short-circuits
  // the whole wizard — so the still-open Claude/ChatGPT/custom funnel was
  // unreachable from that URL too. With the flag it falls through to the
  // ordinary source picker, which is what the isRefresh comment above already
  // promised.
  if (SHOW_CHIBI_PASSPORT_DOOR && refreshId && !refreshKey) {
    return (
      <div style={styles.container}>
        <MarketplaceBackLink />
        <AgentSectionNav />
        <h1 style={styles.pageTitle}>Refresh a Passport</h1>
        <div style={styles.stepContent}>
          <p style={{ color: '#444' }}>
            This browser doesn&rsquo;t hold the API key for that agent, so the passport can&rsquo;t be refreshed from here.
            Open the dashboard on the browser where you registered it, or re-add the key there first.
          </p>
          <button style={styles.primaryButton} onClick={() => navigate('/agents/dashboard')}>Go to dashboard</button>
        </div>
      </div>
    );
  }

  return (
    <div style={styles.container}>
      {/* Chip buttons must FEEL clickable, not just look it (Nina 08-14):
          hover lift + a real pressed state. Inline styles can't do :hover. */}
      <style>{`
        .import-chip-button:hover {
          border-color: #7C4DFF !important;
          background: #f7f4ff !important;
          transform: translateY(-1px);
        }
        .import-chip-button:active { transform: translateY(0); }
      `}</style>
      <MarketplaceBackLink />
      <AgentSectionNav />

      <h1 style={styles.pageTitle}>{isRefresh ? `Refresh ${currentAgent?.name || 'your chibi'}'s Passport` : 'Register Your AI Agent'}</h1>

      {/* Progress Steps */}
      {!result && (
        <div style={styles.progressBar}>
          {STEPS.map((s, i) => {
            // Refresh mode: step 0 (source pick) is locked — the source is
            // forced to 'passport' and re-entering it clobbers the prefilled
            // visibility (review fix C, 08-12).
            const stepLocked = isRefresh && i === 0;
            return (
            <div key={s} style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
              <button
                type="button"
                onClick={async () => { if (stepLocked || passportChecking) return; const from = step; if (from <= 1 && i > 1 && !(await leaveContextStepForward())) { setStep(s => (s === from ? 1 : s)); return; } setStep(s => (s === from ? i : s)); }}
                title={stepLocked ? 'Source is set for a passport refresh' : `Go to ${s}`}
                disabled={stepLocked}
                style={{
                  width: 28,
                  height: 28,
                  padding: 0,
                  borderRadius: '50%',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  fontSize: 13,
                  fontWeight: 600,
                  background: i <= step ? '#7b1fa2' : '#e0e0e0',
                  color: i <= step ? '#fff' : '#666',
                  border: i === step ? '2px solid #8a2be2' : '2px solid transparent',
                  cursor: stepLocked ? 'not-allowed' : 'pointer',
                  opacity: stepLocked ? 0.5 : 1,
                  outline: 'none',
                }}
              >
                {i + 1}
              </button>
              <span
                onClick={async () => { if (stepLocked || passportChecking) return; const from = step; if (from <= 1 && i > 1 && !(await leaveContextStepForward())) { setStep(s => (s === from ? 1 : s)); return; } setStep(s => (s === from ? i : s)); }}
                style={{
                  color: i <= step ? '#7b1fa2' : '#888',
                  fontSize: 13,
                  textTransform: 'capitalize',
                  cursor: stepLocked ? 'default' : 'pointer',
                  opacity: stepLocked ? 0.6 : 1,
                  userSelect: 'none',
                }}
              >
                {s}
              </span>
              {i < STEPS.length - 1 && <div style={{ width: 30, height: 1, background: '#ddd' }} />}
            </div>
            );
          })}
        </div>
      )}

      {/* Step Content */}
      {stepRenderers[step]()}

      {/* Navigation */}
      {!result && (() => {
        // Required-field check used to gate the final "Create Agent" button.
        // Next is intentionally NOT gated — users can browse forward even with empty fields,
        // and we tell them exactly what's missing on the review step.
        const missingFields = [];
        // No source = no agent: the progress dots can jump anywhere from step
        // 0, and the submit's else-branch defaults to the custom register
        // route — a sourceless run minted a BLANK public agent (audit fix
        // 08-14). Whitespace-only instructions counted as filled for the same
        // reason: the checks below now trim.
        if (!source) missingFields.push('A source (step 1: Passport, Claude, ChatGPT, or Custom)');
        if (!name || !name.trim()) missingFields.push('Agent name');
        // Empty stays fine — the field is optional. Only GARBAGE blocks, so we
        // never store an address that cannot be reached.
        if (ownerEmailBad) missingFields.push('A valid email, or clear the email field');
        if (source === 'claude' && !(systemPrompt || '').trim()) missingFields.push('Project instructions');
        if (source === 'chatgpt' && !(gptConfig || '').trim() && !(systemPrompt || '').trim()) missingFields.push('GPT config or instructions');
        if (source === 'custom' && !(systemPrompt || '').trim()) missingFields.push('System prompt');
        if (source === 'passport') {
          if (!passportPreview || !rawPassport.trim()) missingFields.push('A passport (paste your Claude\u2019s reply on the context step, then hit Next)');
          else if (rawPassport !== previewedRaw) missingFields.push('A fresh read of the passport (the paste changed, so step back and hit Next again)');
        }
        const canCreate = missingFields.length === 0 && !isSubmitting;
        // 🕐 SOFT gate (Nina 08-17 round 2: "instead of not allowing them to
        // move forward... it turns red and asks are you sure?"). Two tiers on
        // purpose: the hard list above is absence-means-breakage (no name =
        // no agent); this tier is absence-means-flatter — the port still
        // works, so the wall became a nudge. First Create click with these
        // empty shows the red ask + flips the button to "Create anyway";
        // the second click goes through. Typing in the field clears it.
        // Refresh mode exempt (the field did not exist when older chibis
        // ported). The register API stays optional either way — it is the
        // agents.md door and wizard questions must never 400 an agent.
        const softMissing = [];
        if (!isRefresh && !usuallyUpTo.trim()) {
          softMissing.push('at least one line of what they are usually up to. It becomes their little life in the space');
        }
        const needsSoftAsk = softMissing.length > 0 && !softConfirm;

        return (
          <div>
            {step === 3 && missingFields.length > 0 && (
              <div style={styles.missingFieldsBox} role="status">
                <span style={styles.missingFieldsIcon} aria-hidden="true">&#9888;</span>
                <div>
                  <div style={styles.missingFieldsTitle}>Still needed before you can create your agent:</div>
                  <ul style={styles.missingFieldsList}>
                    {missingFields.map(field => <li key={field}>{field}</li>)}
                  </ul>
                </div>
              </div>
            )}
            {step === 3 && submitError && (
              <div style={{ ...styles.missingFieldsBox, borderColor: '#e57373' }} role="alert">
                <span style={styles.missingFieldsIcon} aria-hidden="true">&#9888;</span>
                <div>{submitError}</div>
              </div>
            )}
            {step === 3 && softConfirm && softMissing.length > 0 && (
              <div style={{ ...styles.missingFieldsBox, borderColor: '#e57373' }} role="alert">
                <span style={styles.missingFieldsIcon} aria-hidden="true">&#10067;</span>
                <div>
                  <div style={{ ...styles.missingFieldsTitle, color: '#e57373' }}>Are you sure?</div>
                  <div>For the best experience, add {softMissing[0]}. One line is plenty! It&rsquo;s on the previous step. Or go ahead as is.</div>
                </div>
              </div>
            )}
            <div style={styles.navRow}>
              <div>
                {/* Refresh mode starts (and floors) at step 1 — Back must not
                    reopen the locked source step (review fix C, 08-12). */}
                {step > (isRefresh ? 1 : 0) && (
                  // Held during an in-flight passport check: Back + a source
                  // re-pick could re-enter the from-step through a different
                  // door and defeat the s===from stale-completion guard
                  // (round-4 finding, latent while the passport door is shut).
                  <button
                    onClick={() => setStep(step - 1)}
                    disabled={passportChecking}
                    style={{ ...styles.primaryButton, opacity: passportChecking ? 0.6 : 1 }}
                  >
                    Back
                  </button>
                )}
              </div>
              <div>
                {step > 0 && step < 3 && (
                  <button
                    onClick={async () => {
                      // The passport check + card fill both ride Next (Nina
                      // 08-14 / 08-20) — shared with the progress dots via
                      // leaveContextStepForward, one gate, two doors. The
                      // functional setStep drops a slow gate's advance if
                      // the user navigated meanwhile (no forward yank).
                      const from = step;
                      if (from === 1 && !(await leaveContextStepForward())) return;
                      setStep(s => (s === from ? s + 1 : s));
                    }}
                    disabled={passportChecking}
                    style={{ ...styles.primaryButton, opacity: passportChecking ? 0.6 : 1 }}
                  >
                    {passportChecking ? 'Checking passport...' : 'Next'}
                  </button>
                )}
                {step === 3 && (
                  <button
                    onClick={() => {
                      // Soft gate: first click with soft fields empty asks,
                      // second click (button now reads "Create anyway") goes.
                      if (canCreate && needsSoftAsk) { setSoftConfirm(true); return; }
                      handleSubmit();
                    }}
                    disabled={!canCreate}
                    style={{ ...styles.primaryButton, opacity: canCreate ? 1 : 0.5, cursor: canCreate ? 'pointer' : 'not-allowed' }}
                  >
                    {isSubmitting
                      ? (source === 'passport' ? 'Importing...' : 'Creating...')
                      : (softConfirm && softMissing.length > 0
                        ? 'Create anyway'
                        // 🐣 Named CTA (Nina 08-28): "Bring Ember!" beats the
                        // generic verb. Renders uppercase via primaryButton
                        // (styles). Fallback when the name field is empty (the
                        // button still shows its label while disabled).
                        : (source === 'passport' ? 'Import Passport' : `Bring ${(name || '').trim() || 'your agent'}!`))}
                  </button>
                )}
              </div>
            </div>
          </div>
        );
      })()}
    </div>
  );
};

const styles = {
  container: {
    maxWidth: 700,
    margin: '0 auto',
    padding: '40px 20px 40px',
    minHeight: '100vh',
    fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
    color: '#111',
  },
  backButton: {
    position: 'fixed',
    top: 18,
    left: 24,
    zIndex: 1000,
    display: 'inline-flex',
    alignItems: 'center',
    gap: 6,
    padding: 0,
    background: 'transparent',
    border: 'none',
    color: '#0a0a0a',
    textShadow: '0 0 10px rgba(255,255,255,0.85), 0 0 18px rgba(255,255,255,0.6)',
    fontSize: '0.7rem',
    fontWeight: 600,
    letterSpacing: '0.1em',
    textTransform: 'uppercase',
    cursor: 'pointer',
    fontFamily: 'inherit',
    transition: 'opacity 0.2s ease',
  },
  pageTitle: {
    fontSize: 28,
    fontWeight: 700,
    marginBottom: 24,
    color: '#111',
  },
  progressBar: {
    display: 'flex',
    gap: 4,
    alignItems: 'center',
    marginBottom: 32,
    flexWrap: 'wrap',
  },
  stepContent: {
    marginBottom: 24,
  },
  stepTitle: {
    fontSize: 20,
    fontWeight: 600,
    marginBottom: 8,
    color: '#111',
  },
  stepDesc: {
    color: '#666',
    marginBottom: 20,
    fontSize: 14,
  },
  sourceGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(3, 1fr)',
    gap: 16,
  },
  sourceCard: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    gap: 10,
    padding: 24,
    borderRadius: 12,
    border: '1px solid #ddd',
    background: '#fff',
    cursor: 'pointer',
    transition: 'all 0.2s',
    color: '#111',
    boxShadow: '0 2px 8px rgba(0,0,0,0.08)',
  },
  sourceCardActive: {
    borderColor: '#7b1fa2',
    background: 'rgba(123, 31, 162, 0.06)',
  },
  sourceMark: {
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    width: 40,
    height: 40,
  },
  sourceLabel: {
    fontSize: 16,
    fontWeight: 600,
  },
  sourceDesc: {
    fontSize: 12,
    color: '#888',
    textAlign: 'center',
  },
  label: {
    display: 'block',
    color: '#333',
    fontSize: 14,
    fontWeight: 500,
    marginBottom: 6,
  },
  hint: {
    color: '#888',
    fontSize: 12,
    marginBottom: 8,
    marginTop: -2,
  },
  optionalTag: {
    color: '#888',
    fontWeight: 400,
    fontSize: 12,
    marginLeft: 6,
  },
  input: {
    width: '100%',
    padding: '10px 12px',
    borderRadius: 8,
    border: '1px solid #ddd',
    background: '#fff',
    color: '#111',
    fontSize: 14,
    outline: 'none',
    boxSizing: 'border-box',
  },
  textarea: {
    width: '100%',
    padding: '10px 12px',
    borderRadius: 8,
    border: '1px solid #ddd',
    background: '#fff',
    color: '#111',
    fontSize: 14,
    outline: 'none',
    resize: 'vertical',
    fontFamily: 'inherit',
    boxSizing: 'border-box',
  },
  select: {
    width: '100%',
    padding: '10px 12px',
    borderRadius: 8,
    border: '1px solid #ddd',
    background: '#fff',
    color: '#111',
    fontSize: 14,
    outline: 'none',
  },
  typeButton: {
    padding: '8px 16px',
    borderRadius: 8,
    border: '1px solid #ddd',
    background: '#fff',
    color: '#666',
    fontSize: 13,
    cursor: 'pointer',
    transition: 'all 0.2s',
  },
  typeButtonActive: {
    borderColor: '#7b1fa2',
    background: 'rgba(123, 31, 162, 0.08)',
    color: '#7b1fa2',
  },
  tagsGrid: {
    display: 'flex',
    gap: 8,
    flexWrap: 'wrap',
  },
  tagChip: {
    padding: '6px 14px',
    borderRadius: 16,
    border: '1px solid #ddd',
    background: '#fff',
    color: '#666',
    fontSize: 13,
    cursor: 'pointer',
    transition: 'all 0.2s',
  },
  tagInput: {
    padding: '6px 14px',
    borderRadius: 16,
    border: '1px dashed #bbb',
    background: '#fff',
    color: '#333',
    fontSize: 13,
    outline: 'none',
    width: 130,
  },
  tagChipActive: {
    background: 'rgba(123, 31, 162, 0.1)',
    borderColor: '#7b1fa2',
    color: '#7b1fa2',
  },
  inviteBox: {
    maxHeight: 200,
    overflowY: 'auto',
    whiteSpace: 'pre-wrap',
    padding: '12px 14px',
    borderRadius: 8,
    border: '1px solid #e0e0e0',
    background: '#fafafa',
    color: '#444',
    fontSize: 12,
    lineHeight: 1.5,
    fontFamily: 'ui-monospace, SFMono-Regular, Menlo, monospace',
  },
  previewBox: {
    marginTop: 20,
    padding: '14px 16px',
    borderRadius: 10,
    border: '1px solid rgba(123, 31, 162, 0.35)',
    background: 'rgba(123, 31, 162, 0.04)',
  },
  previewHeader: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'baseline',
    gap: 12,
    flexWrap: 'wrap',
    marginBottom: 10,
    color: '#111',
    fontSize: 14,
  },
  previewField: {
    padding: '8px 0',
    borderTop: '1px solid rgba(0,0,0,0.06)',
  },
  warningsBox: {
    background: '#fff8e6',
    border: '1px solid #f5d77a',
    borderRadius: 8,
    padding: '10px 12px',
    marginBottom: 10,
    color: '#5a4500',
    fontSize: 13,
    lineHeight: 1.6,
  },
  staleNote: {
    background: '#fff8e6',
    border: '1px solid #f5d77a',
    borderRadius: 8,
    padding: '8px 12px',
    marginBottom: 10,
    color: '#5a4500',
    fontSize: 13,
    fontWeight: 600,
  },
  passportErrorBox: {
    marginTop: 14,
    padding: '10px 14px',
    borderRadius: 8,
    border: '1px solid #ef9a9a',
    background: '#fdecea',
    color: '#b71c1c',
    fontSize: 13.5,
    lineHeight: 1.5,
  },
  // 🖱️ Read as CLICKABLE (Nina 08-14 screenshot): dashed grey border + #888
  // on transparent read as a disabled placeholder, so nobody tried them.
  // Solid border, real ink, a surface to click — the page's secondaryButton
  // language at chip scale. Hover/active handled by .import-chip-button.
  addFileButton: {
    padding: '9px 16px',
    borderRadius: 8,
    border: '1px solid #c9c9d4',
    background: '#fff',
    color: '#333',
    fontSize: 13,
    fontWeight: 600,
    fontFamily: 'inherit',
    cursor: 'pointer',
    boxShadow: '0 1px 2px rgba(0,0,0,0.06)',
    transition: 'border-color 0.15s ease, background 0.15s ease, transform 0.15s ease',
  },
  // The card panel's quiet manual-mode escape: reads as a link, sits in a
  // hint line, never competes with the primary copy/paste/Next path.
  inlineLinkButton: {
    background: 'none',
    border: 'none',
    padding: 0,
    color: '#5b32d6',
    fontSize: 'inherit',
    fontFamily: 'inherit',
    fontWeight: 600,
    textDecoration: 'underline',
    cursor: 'pointer',
  },
  // 🎤 "No project instructions" helper panel
  helpPanel: {
    marginTop: 10,
    borderTop: '1px solid #eee',
    paddingTop: 10,
  },
  helpToggle: {
    display: 'inline-flex',
    alignItems: 'center',
    gap: 7,
    background: 'transparent',
    border: 'none',
    padding: 0,
    color: '#5b32d6',
    fontSize: 13,
    fontWeight: 600,
    fontFamily: 'inherit',
    cursor: 'pointer',
  },
  helpBody: {
    marginTop: 10,
    background: '#faf8ff',
    border: '1px solid #e6def9',
    borderRadius: 10,
    padding: '14px 16px',
  },
  helpLead: {
    margin: '0 0 10px',
    fontSize: 13,
    lineHeight: 1.55,
    color: '#444',
  },
  helpPre: {
    // The interview is long: give it its own scroll box instead of letting it
    // push Next off the screen.
    maxHeight: 220,
    overflowY: 'auto',
    background: '#fff',
    border: '1px solid #e6def9',
    borderRadius: 8,
    padding: '10px 12px',
    margin: '0 0 12px',
    fontSize: 11.5,
    lineHeight: 1.5,
    color: '#333',
    whiteSpace: 'pre-wrap',
    wordBreak: 'break-word',
    fontFamily: 'ui-monospace, SFMono-Regular, Menlo, monospace',
  },
  reviewGrid: {
    display: 'grid',
    gridTemplateColumns: '1fr 1fr',
    gap: 12,
    marginTop: 24,
  },
  reviewItem: {
    padding: 12,
    borderRadius: 8,
    background: '#f5f5f5',
    border: '1px solid #e0e0e0',
  },
  reviewLabel: {
    display: 'block',
    fontSize: 11,
    color: '#888',
    marginBottom: 4,
    textTransform: 'uppercase',
    letterSpacing: 0.5,
  },
  reviewValue: {
    fontSize: 14,
    color: '#111',
  },
  successBox: {
    textAlign: 'center',
    padding: 32,
  },
  apiKeyBox: {
    background: '#fff8e1',
    border: '1px solid #ffe082',
    borderRadius: 8,
    padding: 16,
    textAlign: 'left',
  },
  apiKeyCode: {
    display: 'block',
    marginTop: 8,
    padding: '10px 12px',
    background: '#fafafa',
    border: '1px solid #e0e0e0',
    borderRadius: 6,
    fontFamily: 'monospace',
    fontSize: 13,
    color: '#e65100',
    wordBreak: 'break-all',
    userSelect: 'all',
  },
  quickstartHint: {
    display: 'inline-flex',
    alignItems: 'center',
    gap: 8,
    marginTop: 18,
    padding: '10px 14px',
    borderRadius: 8,
    background: 'rgba(123, 31, 162, 0.06)',
    border: '1px solid rgba(123, 31, 162, 0.2)',
    color: '#555',
    fontSize: 13.5,
    cursor: 'pointer',
    transition: 'background 0.2s ease',
  },
  quickstartHintLink: {
    color: '#7b1fa2',
    fontWeight: 600,
    textDecoration: 'underline',
    textUnderlineOffset: 2,
  },
  navRow: {
    display: 'flex',
    gap: 12,
    justifyContent: 'space-between',
    alignItems: 'center',
    marginTop: 8,
  },
  ctaButton: {
    padding: '12px 24px',
    borderRadius: 10,
    border: '1px solid #0a0a0a',
    background: '#fff',
    color: '#0a0a0a',
    fontFamily: '-apple-system, BlinkMacSystemFont, "Inter", "Segoe UI", Roboto, sans-serif',
    fontSize: '0.75rem',
    fontWeight: 700,
    letterSpacing: '0.12em',
    textTransform: 'uppercase',
    cursor: 'pointer',
  },
  primaryButton: {
    padding: '12px 24px',
    borderRadius: 10,
    border: '1px solid #0a0a0a',
    background: '#fff',
    color: '#0a0a0a',
    fontFamily: '-apple-system, BlinkMacSystemFont, "Inter", "Segoe UI", Roboto, sans-serif',
    fontSize: '0.75rem',
    fontWeight: 700,
    letterSpacing: '0.12em',
    textTransform: 'uppercase',
    cursor: 'pointer',
  },
  secondaryButton: {
    padding: '12px 24px',
    borderRadius: 8,
    border: '1px solid #ddd',
    background: '#fff',
    color: '#333',
    fontSize: 15,
    cursor: 'pointer',
  },
  missingFieldsBox: {
    display: 'flex',
    gap: 12,
    alignItems: 'flex-start',
    background: '#fff8e6',
    border: '1px solid #f5d77a',
    borderRadius: 10,
    padding: '12px 16px',
    marginBottom: 16,
    color: '#5a4500',
    fontSize: 14,
  },
  missingFieldsIcon: {
    fontSize: 18,
    lineHeight: '20px',
    flexShrink: 0,
  },
  missingFieldsTitle: {
    fontWeight: 600,
    marginBottom: 4,
  },
  missingFieldsList: {
    margin: 0,
    paddingLeft: 18,
  },
};

export default AgentImportPage;
